import { useState, useRef, useCallback, useEffect } from "react";

// ── Questions ────────────────────────────────────────────────────────────────
const ALL_MODULES = [
  { id: "travel2026", label: "Travel 2026", url: "travel2026", questions: [{ id: "q1", text: "We're halfway through 2026. Where do you want to travel before the year is out?" }] },
  { id: "parents", label: "Your Parents", url: "parents", questions: [{ id: "q2", text: "What were your parents like when you were little?" }] },
  { id: "oldestrelative", label: "Oldest Relative", url: "oldestrelative", questions: [{ id: "q3", text: "Who was the oldest relative you remember knowing?" }] },
  { id: "familystory", label: "Family Story", url: "familystory", questions: [{ id: "q4", text: "Please tell me a family story that leaves everyone laughing." }] },
  { id: "differently", label: "Done Differently", url: "differently", questions: [{ id: "q5", text: "Are there any big things you'd do differently if you had the chance?" }] },
  { id: "decision", label: "Best Decision", url: "decision", questions: [{ id: "q6", text: "If you had to pick one big decision that paid off for you, what would it be?" }] },
  { id: "recipe", label: "Family Recipe", url: "recipe", questions: [{ id: "q7", text: "Can you think of a family recipe to pass down? What is it?" }] },
];

function formatTime(s) {
  const m = Math.floor(s / 60).toString().padStart(2,"0");
  return `${m}:${(s%60).toString().padStart(2,"0")}`;
}

function getURLParams() {
  const p = new URLSearchParams(window.location.search);
  const m = p.get("module");
  const mod = m ? ALL_MODULES.find(mod => mod.url === m) || null : null;
  return {
    module: mod,
    fromEmail: p.get("from") || "",
    fromName: p.get("fromname") || "",
    recipientName: p.get("recipientname") || "",
    recipientEmail: p.get("recipientemail") || "",
    customQuestion: p.get("q") || "",
    eventMode: p.get("mode") === "event",
    support: p.get("support") === "1",
  };
}

// ── Main App ─────────────────────────────────────────────────────────────────
export default function LifeStoryMagicV2() {
  const urlParams = getURLParams();
  const urlModule = urlParams.module;

  // If the module has only one question, treat it as a custom question
  const urlModuleQ = null; // All links now use ?q= directly
  const urlCustomQ = getURLParams().customQuestion || urlModuleQ;
  const isEventMode = getURLParams().eventMode;
  const [customQuestionText, setCustomQuestionText] = useState(urlCustomQ || "");
  const isCustomMode = !!urlCustomQ || false;
  const [mode, setMode] = useState(urlModule || urlCustomQ ? "send_interview" : null);
  const [screen, setScreen] = useState(urlModule || urlCustomQ ? "welcome" : "home");
  const [selectedModule, setSelectedModule] = useState(urlModule || null);
  const [subjectName, setSubjectName] = useState("");
  const [interviewerName, setInterviewerName] = useState(urlParams.fromName || localStorage.getItem('lsm_u1_name') || "");
  const [interviewerEmail, setInterviewerEmail] = useState(urlParams.fromEmail || localStorage.getItem('lsm_u1_email') || "");
  const [recipientName, setRecipientName] = useState(urlParams.recipientName || localStorage.getItem('lsm_last_recipient') || "");
  const [recipientEmail, setRecipientEmail] = useState(urlParams.recipientEmail || "");
  const [recipientHistory, setRecipientHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lsm_recipient_history') || '[]'); } catch(e) { return []; }
  });

  // Interview state
  const [qIndex, setQIndex] = useState(0);
  const [markers, setMarkers] = useState([]);
  const [transcript, setTranscript] = useState([]);
  const [liveText, setLiveText] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingElapsed, setRecordingElapsed] = useState(0);
  const [recMode, setRecMode] = useState(null); // null, audio, video
  const [recChunks, setRecChunks] = useState([]);
  const [mediaRecording, setMediaRecording] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [hasAnswered, setHasAnswered] = useState(false);
  const [interviewStartTime, setInterviewStartTime] = useState(null);

  // Download / email state
  const [emailInput, setEmailInput] = useState(urlParams.recipientEmail || "");
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [ccConsent, setCcConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [additionalRecipients, setAdditionalRecipients] = useState("");
  const [audioBlob, setAudioBlob] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [cleanedTranscript, setCleanedTranscript] = useState("");
  const [isCleaning, setIsCleaning] = useState(false);
  const [cleaningError, setCleaningError] = useState(false);
  const [lastWords, setLastWords] = useState("");
  const [welcomeSeen, setWelcomeSeen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    if (!urlCustomQ && !localStorage.getItem('lsm_visited')) {
      setInfoOpen(true);
      localStorage.setItem('lsm_visited', 'true');
    }
  }, []);
  const [alphaOpen, setAlphaOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(urlParams.support || false);
  const [qspOpen, setQspOpen] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [selectedQSP, setSelectedQSP] = useState(null);
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [signupDone, setSignupDone] = useState(false);
  const [typeMode, setTypeMode] = useState(false);
  const [typedAnswer, setTypedAnswer] = useState("");
  const [welcomeRecordStarted, setWelcomeRecordStarted] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  // ── Event Mode State ──────────────────────────────────────────────────────
  const [eventScreen, setEventScreen] = useState("setup"); // setup, question, responses, result
  const [eventQuestion, setEventQuestion] = useState("");
  const [eventQuestionTranscript, setEventQuestionTranscript] = useState("");
  const [eventResponses, setEventResponses] = useState([]); // array of raw transcripts
  const [eventCurrentTranscript, setEventCurrentTranscript] = useState("");
  const [isEventRecording, setIsEventRecording] = useState(false);
  const [isEventTranscribing, setIsEventTranscribing] = useState(false);
  const [eventRecordingElapsed, setEventRecordingElapsed] = useState(0);
  const [eventResult, setEventResult] = useState("");
  const [isAggregating, setIsAggregating] = useState(false);
  const [eventOrganizerName, setEventOrganizerName] = useState("");
  const [eventOrganizerEmail, setEventOrganizerEmail] = useState("");
  const eventMediaRecorderRef = useRef(null);
  const eventChunksRef = useRef([]);
  const eventStreamRef = useRef(null);
  const eventTimerRef = useRef(null);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const [voiceComplete, setVoiceComplete] = useState(false);
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [voiceError, setVoiceError] = useState(false);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [isGeneratingQuestion, setIsGeneratingQuestion] = useState(false);
  const [voiceElapsed, setVoiceElapsed] = useState(0);
  const voiceSpeechRef = useRef(null);
  const voiceTimerRef = useRef(null);
  const voiceFinalRef = useRef("");
  const voiceMediaRecorderRef = useRef(null);
  const voiceChunksRef = useRef([]);
  const voiceStreamRef = useRef(null);
  const [cycleIdx, setCycleIdx] = useState(0);
  const [cycleFade, setCycleFade] = useState(true);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null); // 'positive' or 'negative'
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [totalWordCount, setTotalWordCount] = useState(0);

  const speechRef = useRef(null);
  const speechTimerRef = useRef(null);
  const pauseTimerRef = useRef(null);
  const finalTextRef = useRef("");
  const capturedAnswerRef = useRef(""); // mirrors currentAnswer synchronously for re-recording
  const answerMediaRecorderRef = useRef(null);
  const answerChunksRef = useRef([]);
  const answerStreamRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const streamRef = useRef(null);

  const questions = selectedModule ? selectedModule.questions : [];
  const currentQ = questions[qIndex];

  // ── Whisper Recording ──────────────────────────────────────────────────────
  const playTone = (freq, duration) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch(e) {}
  };

  const startSpeech = useCallback(async () => {
    try {
      // Haptic + tone on record start
      if (navigator.vibrate) navigator.vibrate(80);
      playTone(660, 0.15);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      answerStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" :
                       MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/ogg";
      const mr = new MediaRecorder(stream, { mimeType });
      answerChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) answerChunksRef.current.push(e.data); };
      mr.start(1000);
      answerMediaRecorderRef.current = mr;
      setIsRecording(true);
      setHasAnswered(true);
      setRecordingElapsed(1);
      recordingTimerRef.current = setInterval(() => {
        setRecordingElapsed(p => p + 1);
      }, 1000);
      // Auto-stop at 59 seconds
      if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = setTimeout(() => {
        if (answerMediaRecorderRef.current) {
          answerMediaRecorderRef.current.stop();
        }
      }, 59000);
    } catch (err) {
      alert("Could not access microphone. Please allow microphone access and try again.");
    }
  }, []);

  const stopSpeech = useCallback(async () => {
    // Haptic + tone on record stop
    if (navigator.vibrate) navigator.vibrate([50, 40, 50]);
    playTone(440, 0.2);
    clearInterval(recordingTimerRef.current);
    if (autoStopTimerRef.current) { clearTimeout(autoStopTimerRef.current); autoStopTimerRef.current = null; }
    setIsRecording(false);

    return new Promise((resolve) => {
      if (!answerMediaRecorderRef.current) {
        if (answerStreamRef.current) {
          answerStreamRef.current.getTracks().forEach(t => t.stop());
          answerStreamRef.current = null;
        }
        resolve(""); return;
      }
      const mr = answerMediaRecorderRef.current;
      const mimeType = mr.mimeType;
      mr.onstop = async () => {
        // Stop tracks AFTER recorder has flushed final chunk
        if (answerStreamRef.current) {
          answerStreamRef.current.getTracks().forEach(t => t.stop());
          answerStreamRef.current = null;
        }
        answerMediaRecorderRef.current = null;
        const blob = new Blob(answerChunksRef.current, { type: mimeType });
        answerChunksRef.current = [];

        // Size check — 20MB limit
        const MAX_SIZE = 20 * 1024 * 1024;
        if (blob.size > MAX_SIZE) {
          setIsTranscribing(false);
          resolve("[Recording too long to transcribe — please try a shorter answer or use the typing option]");
          return;
        }

        setIsTranscribing(true);

        // Hard timeout — 55 seconds
        const transcribeTimeout = setTimeout(() => {
          setIsTranscribing(false);
          resolve(capturedAnswerRef.current || "[Transcription timed out — please try again or use the typing option]");
        }, 55000);

        try {
          const reader = new FileReader();
          reader.onloadend = async () => {
            try {
              const base64 = reader.result.split(",")[1];
              const controller = new AbortController();
              const fetchTimeout = setTimeout(() => controller.abort(), 54000);
              const resp = await fetch("/.netlify/functions/transcribe-audio", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ audioData: base64, mimeType, fileName: "answer.webm" }),
                signal: controller.signal
              });
              clearTimeout(fetchTimeout);
              clearTimeout(transcribeTimeout);
              if (resp.ok) {
                const data = await resp.json();
                const transcript = data.transcript || "";
                const existing = capturedAnswerRef.current ? capturedAnswerRef.current + " " : "";
                const combined = (existing + transcript).trim();
                setCurrentAnswer(combined);
                capturedAnswerRef.current = combined;
                resolve(combined);
              } else {
                resolve(capturedAnswerRef.current || "");
              }
            } catch(e) {
              clearTimeout(transcribeTimeout);
              resolve(capturedAnswerRef.current || "");
            }
            setIsTranscribing(false);
          };
          reader.onerror = () => { clearTimeout(transcribeTimeout); setIsTranscribing(false); resolve(""); };
          reader.readAsDataURL(blob);
        } catch (e) {
          clearTimeout(transcribeTimeout);
          setIsTranscribing(false);
          resolve(capturedAnswerRef.current || "");
        }
      };
      mr.stop();
    });
  }, []);

  // ── Media Recording ──────────────────────────────────────────────────────
  const startMediaRecording = useCallback(async (type) => {
    try {
      const constraints = type === "video"
        ? { audio: true, video: { facingMode: "user" } }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (type === "video") {
        // Use setTimeout to ensure DOM element is mounted before attaching stream
        setTimeout(() => {
          if (videoPreviewRef.current) {
            videoPreviewRef.current.srcObject = stream;
            videoPreviewRef.current.muted = true;
            videoPreviewRef.current.play().catch(()=>{});
          }
        }, 100);
      }
      const mimeType = type === "video"
        ? (MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/webm")
        : (MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm");
      const mr = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start(1000);
      mediaRecorderRef.current = mr;
      setMediaRecording(true);
      setRecMode(type);
      setInterviewStartTime(Date.now());
    } catch (err) {
      alert("Could not access microphone/camera. Please allow access and try again.");
    }
  }, []);

  const stopMediaRecording = useCallback(() => {
    return new Promise(resolve => {
      // Always stop stream tracks to turn off camera light
      const stopStream = () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = null;
        }
      };

      if (!mediaRecorderRef.current) { stopStream(); resolve(null); return; }
      const mr = mediaRecorderRef.current;
      const mode = recMode;
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        if (mode === "video") setVideoBlob(blob);
        else setAudioBlob(blob);
        stopStream();
        resolve(blob);
      };
      try { mr.stop(); } catch(e) { stopStream(); resolve(null); }
      mediaRecorderRef.current = null;
      setMediaRecording(false);
    });
  }, [recMode]);

  // ── Interview controls ───────────────────────────────────────────────────
  const startVoiceQuestion = useCallback(async () => {
    try {
      if (navigator.vibrate) navigator.vibrate(80);
      playTone(660, 0.15);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" :
                       MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/ogg";
      const mr = new MediaRecorder(stream, { mimeType });
      voiceChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) voiceChunksRef.current.push(e.data); };
      mr.start(1000);
      voiceMediaRecorderRef.current = mr;
      setIsVoiceRecording(true);
      setVoiceElapsed(0);
      voiceTimerRef.current = setInterval(() => setVoiceElapsed(p => p+1), 1000);
    } catch (err) {
      alert("Could not access microphone. Please allow microphone access and try again.");
    }
  }, []);

  const stopVoiceAndGenerate = useCallback(async () => {
    if (navigator.vibrate) navigator.vibrate([50, 40, 50]);
    playTone(440, 0.2);
    clearInterval(voiceTimerRef.current);
    if (voiceStreamRef.current) {
      voiceStreamRef.current.getTracks().forEach(t => t.stop());
      voiceStreamRef.current = null;
    }
    setIsVoiceRecording(false);

    if (!voiceMediaRecorderRef.current) {
      setVoiceError(true);
      setShowTypeForm(true);
      return;
    }

    setIsGeneratingQuestion(true);

    const mr = voiceMediaRecorderRef.current;
    const mimeType = mr.mimeType;

    const audioBlob = await new Promise(resolve => {
      mr.onstop = () => {
        const blob = new Blob(voiceChunksRef.current, { type: mimeType });
        voiceChunksRef.current = [];
        voiceMediaRecorderRef.current = null;
        resolve(blob);
      };
      mr.stop();
    });

    try {
      // First transcribe with Whisper
      const reader = new FileReader();
      const base64 = await new Promise(resolve => {
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(audioBlob);
      });

      const transcribeResp = await fetch("/.netlify/functions/transcribe-audio", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ audioData: base64, mimeType, fileName: "question.webm" })
      });

      if (!transcribeResp.ok) {
        setVoiceError(true);
        setShowTypeForm(true);
        setIsGeneratingQuestion(false);
        return;
      }

      const transcribeData = await transcribeResp.json();
      const raw = transcribeData.transcript || "";

      if (!raw.trim()) {
        setVoiceError(true);
        setShowTypeForm(true);
        setIsGeneratingQuestion(false);
        return;
      }

      // Then send to generate-question to extract name and clean question
      const genResp = await fetch("/.netlify/functions/generate-question", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rawText: raw })
      });

      if (genResp.ok) {
        const data = await genResp.json();
        if (data.name) setRecipientName(data.name);
        if (data.question) setCustomQuestionText(data.question.slice(0, 100));
        setVoiceComplete(true);
        setShowTypeForm(true);
      } else {
        setVoiceError(true);
        setShowTypeForm(true);
      }
    } catch (e) {
      console.error("voice question error", e);
      setVoiceError(true);
      setShowTypeForm(true);
    }
    setIsGeneratingQuestion(false);
  }, []);

  // ── Event Mode Recording ─────────────────────────────────────────────────
  const startEventRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      eventStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" :
                       MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/ogg";
      const mr = new MediaRecorder(stream, { mimeType });
      eventChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) eventChunksRef.current.push(e.data); };
      mr.start(1000);
      eventMediaRecorderRef.current = mr;
      setIsEventRecording(true);
      setEventRecordingElapsed(0);
      eventTimerRef.current = setInterval(() => setEventRecordingElapsed(p => p+1), 1000);
    } catch(err) {
      alert("Could not access microphone.");
    }
  }, []);

  const stopEventRecording = useCallback(async () => {
    clearInterval(eventTimerRef.current);
    if (eventStreamRef.current) { eventStreamRef.current.getTracks().forEach(t=>t.stop()); eventStreamRef.current=null; }
    setIsEventRecording(false);

    return new Promise(resolve => {
      if (!eventMediaRecorderRef.current) { resolve(""); return; }
      const mr = eventMediaRecorderRef.current;
      const mimeType = mr.mimeType;
      mr.onstop = async () => {
        eventMediaRecorderRef.current = null;
        const blob = new Blob(eventChunksRef.current, { type: mimeType });
        eventChunksRef.current = [];
        setIsEventTranscribing(true);
        try {
          const reader = new FileReader();
          reader.onloadend = async () => {
            const base64 = reader.result.split(",")[1];
            const resp = await fetch("/.netlify/functions/transcribe-audio", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ audioData: base64, mimeType, fileName: "event.webm" })
            });
            if (resp.ok) {
              const data = await resp.json();
              resolve(data.transcript || "");
            } else { resolve(""); }
            setIsEventTranscribing(false);
          };
          reader.readAsDataURL(blob);
        } catch(e) { setIsEventTranscribing(false); resolve(""); }
      };
      mr.stop();
    });
  }, []);

  const stopEventRecordingWithDownload = useCallback(async (responseNumber) => {
    clearInterval(eventTimerRef.current);
    if (eventStreamRef.current) { eventStreamRef.current.getTracks().forEach(t=>t.stop()); eventStreamRef.current=null; }
    setIsEventRecording(false);

    return new Promise(resolve => {
      if (!eventMediaRecorderRef.current) { resolve(""); return; }
      const mr = eventMediaRecorderRef.current;
      const mimeType = mr.mimeType;
      mr.onstop = async () => {
        eventMediaRecorderRef.current = null;
        const blob = new Blob(eventChunksRef.current, { type: mimeType });
        eventChunksRef.current = [];

        // Auto-download audio backup
        const ext = mimeType.includes("mp4") ? "m4a" : "webm";
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `event_response_${responseNumber}_${new Date().toISOString().slice(0,19).replace(/:/g,"-")}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);

        // Check size before transcribing — skip if over 4MB to avoid request limit
        const MAX_SIZE = 4 * 1024 * 1024;
        if (blob.size > MAX_SIZE) {
          resolve("[Audio saved — too long to auto-transcribe. Type a summary or transcribe the downloaded file manually.]");
          return;
        }

        // Transcribe
        setIsEventTranscribing(true);
        const transcribeTimeout = setTimeout(() => {
          setIsEventTranscribing(false);
          resolve("[Transcription timed out — audio file was saved to your device]");
        }, 25000);
        try {
          const reader = new FileReader();
          reader.onloadend = async () => {
            try {
              const base64 = reader.result.split(",")[1];
              const controller = new AbortController();
              const fetchTimeout = setTimeout(() => controller.abort(), 24000);
              const resp = await fetch("/.netlify/functions/transcribe-audio", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ audioData: base64, mimeType, fileName: `event_${responseNumber}.webm` }),
                signal: controller.signal
              });
              clearTimeout(fetchTimeout);
              clearTimeout(transcribeTimeout);
              if (resp.ok) {
                const data = await resp.json();
                resolve(data.transcript || "[No transcript — audio file saved to device]");
              } else {
                resolve("[Transcription failed — audio file saved to device]");
              }
            } catch(e) {
              clearTimeout(transcribeTimeout);
              resolve("[Transcription error — audio file saved to device]");
            }
            setIsEventTranscribing(false);
          };
          reader.onerror = () => { clearTimeout(transcribeTimeout); setIsEventTranscribing(false); resolve("[Could not read audio]"); };
          reader.readAsDataURL(blob);
        } catch(e) { clearTimeout(transcribeTimeout); setIsEventTranscribing(false); resolve(""); }
      };
      mr.stop();
    });
  }, []);

  const aggregateEventResponses = useCallback(async (responses, question) => {
    setIsAggregating(true);
    try {
      const resp = await fetch("/.netlify/functions/aggregate-event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, responses })
      });
      if (resp.ok) {
        const data = await resp.json();
        setEventResult(data.formatted || "");
      }
    } catch(e) { console.error(e); }
    setIsAggregating(false);
  }, []);

  const startInterview = useCallback(() => {
    setQIndex(0);
    setMarkers([]);
    setTranscript([]);
    setCurrentAnswer("");
    capturedAnswerRef.current = "";
    setHasAnswered(false);
    setIsRecording(false);
    setRecordingElapsed(0);
    setTypedAnswer("");
    setScreen("interview");
    setElapsed(0);
  }, []);

  const logMarker = useCallback((idx) => {
    if (!interviewStartTime) return;
    const sec = Math.floor((Date.now() - interviewStartTime) / 1000);
    setMarkers(prev => [...prev, { qIndex: idx, time: sec, question: questions[idx]?.text }]);
  }, [interviewStartTime, questions]);

  const commitCurrent = useCallback(() => {
    const ans = currentAnswer.trim() || capturedAnswerRef.current.trim();
    if (ans && currentQ) {
      setTranscript(prev => {
        const exists = prev.findIndex(e => e.id === currentQ.id);
        const entry = { id: currentQ.id, question: currentQ.text, answer: ans, time: interviewStartTime ? Math.floor((Date.now()-interviewStartTime)/1000) : 0 };
        if (exists >= 0) { const n=[...prev]; n[exists]=entry; return n; }
        return [...prev, entry];
      });
    }
    setCurrentAnswer("");
    capturedAnswerRef.current = "";
    setHasAnswered(false);
    setRecordingElapsed(0);
  }, [currentAnswer, currentQ, interviewStartTime]);

  const goNext = useCallback(() => {
    if (isCustomMode) {
      // Custom mode: save the one answer and go to finishing
      const ans = currentAnswer.trim() || capturedAnswerRef.current.trim();
      if (ans) {
        setTranscript([{ id:"custom_q", question: customQuestionText, answer: ans }]);
      }
      setScreen("finishing");
      return;
    }
    commitCurrent();
    if (qIndex < questions.length - 1) {
      const next = qIndex + 1;
      logMarker(next);
      setQIndex(next);
    } else {
      setScreen("finishing");
    }
  }, [commitCurrent, qIndex, questions.length, logMarker, isCustomMode, customQuestionText, currentAnswer]);

  const goBack = useCallback(() => {
    if (qIndex === 0) return;
    commitCurrent();
    setQIndex(q => q - 1);
  }, [commitCurrent, qIndex]);

  const goSkip = useCallback(() => {
    if (isRecording) {
      if (answerMediaRecorderRef.current) try { answerMediaRecorderRef.current.stop(); } catch {}
      if (answerStreamRef.current) { answerStreamRef.current.getTracks().forEach(t=>t.stop()); answerStreamRef.current=null; }
      clearInterval(recordingTimerRef.current);
      setIsRecording(false);
    }
    setCurrentAnswer(""); capturedAnswerRef.current = ""; setHasAnswered(false); setRecordingElapsed(0);
    if (qIndex < questions.length - 1) {
      const next = qIndex + 1;
      logMarker(next);
      setQIndex(next);
    } else {
      setScreen("finishing");
    }
  }, [isRecording, qIndex, questions.length, logMarker]);

  const toggleSpeech = useCallback(async () => {
    if (isRecording) {
      const ans = await stopSpeech();
      if (ans && ans.trim().length > 5) {
        const q = customQuestionText;
        const entry = { id:"custom_q", question: q, answer: ans };
        setTranscript([entry]);
        setCurrentAnswer(ans);
        capturedAnswerRef.current = ans;
        setHasAnswered(true);
        // Do NOT auto-advance — user presses Done ✓
        cleanTranscriptWithClaude(q + "\n\n" + ans);
      }
    } else {
      await startSpeech();
    }
  }, [isRecording, startSpeech, stopSpeech, customQuestionText]);



  // ── Timer ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (mediaRecording) {
      timerRef.current = setInterval(() => setElapsed(p => p+1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [mediaRecording]);


  // Warn before leaving page in event mode
  useEffect(() => {
    if (!isEventMode) return;
    const handler = (e) => {
      if (eventResponses.length > 0) {
        e.preventDefault();
        e.returnValue = "You have event responses that haven't been compiled yet. Are you sure you want to leave?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isEventMode, eventResponses.length]);

  // Restore event session from localStorage if available
  useEffect(() => {
    if (!isEventMode) return;
    try {
      const saved = localStorage.getItem("lsm_event_responses");
      if (saved) {
        const data = JSON.parse(saved);
        if (data.responses && data.responses.length > 0) {
          const restore = window.confirm(`Found ${data.responses.length} saved response${data.responses.length!==1?"s":""} from a previous session (saved ${new Date(data.savedAt).toLocaleTimeString()}). Restore them?`);
          if (restore) {
            if (data.question) setEventQuestion(data.question);
            if (data.responses) setEventResponses(data.responses);
            if (data.organizer) setEventOrganizerName(data.organizer);
            if (data.email) setEventOrganizerEmail(data.email);
            setEventScreen("responses");
          } else {
            localStorage.removeItem("lsm_event_responses");
          }
        }
      }
    } catch(e) {}
  }, [isEventMode]);

  // Auto-populate email from URL when reaching finishing screen
  useEffect(() => {
    if (urlParams.recipientEmail && !emailInput) {
      setEmailInput(urlParams.recipientEmail);
    }
  }, [screen]);

  useEffect(() => () => {
    if (voiceMediaRecorderRef.current) try { voiceMediaRecorderRef.current.stop(); } catch {}
    if (voiceStreamRef.current) voiceStreamRef.current.getTracks().forEach(t=>t.stop());
    clearInterval(timerRef.current);
    clearInterval(recordingTimerRef.current);
    if (answerMediaRecorderRef.current) try { answerMediaRecorderRef.current.stop(); } catch {}
    if (answerStreamRef.current) answerStreamRef.current.getTracks().forEach(t=>t.stop());
    if (mediaRecorderRef.current) try { mediaRecorderRef.current.stop(); } catch {}
    if (streamRef.current) streamRef.current.getTracks().forEach(t=>t.stop());
  }, []);

  // ── Transcript text ──────────────────────────────────────────────────────
  const cleanTranscriptWithClaude = async (raw) => {
    setIsCleaning(true);
    setCleaningError(false);
    try {
      const resp = await fetch("/.netlify/functions/clean-transcript", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transcript: raw })
      });
      if (!resp.ok) throw new Error("Function error");
      const data = await resp.json();
      setCleanedTranscript(data.cleaned);
      setIsCleaning(false);
      return data.cleaned;
    } catch (e) {
      setCleaningError(true);
      setIsCleaning(false);
      return raw; // fall back to raw on error
    }
  };

  const buildTranscript = () => {
    const sub = recipientName || subjectName || "Someone";
    const body = transcript.map(e => ">>" + e.question + "\n\n" + e.answer).join("\n\n---\n\n");
    return body;
  };

  const rawTranscriptRef = { current: "" };

  const copyEmailText = (bodyText) => {
    const footer = "\n\n---\nLife Story Magic — Please help us keep this free! https://app.lifestorymagic.com/?support=1";
    const full = bodyText + footer;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(full).then(() => {
        alert("Copied! Open your email app, paste into the body, and write a subject line.");
      }).catch(() => {
        const ta = document.createElement("textarea");
        ta.value = full;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        alert("Copied! Open your email app, paste into the body, and write a subject line.");
      });
    } else {
      const ta = document.createElement("textarea");
      ta.value = full;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      alert("Copied! Open your email app, paste into the body, and write a subject line.");
    }
  };
  const downloadTranscript = () => {
    // Always use the raw buildTranscript — never touches cleanedTranscript state
    const rawText = buildTranscript();
    const warning = "RAW BACKUP TRANSCRIPT\n" + "═".repeat(50) + "\nWARNING: This is the unedited, unpunctuated raw version.\nThe cleaned version was sent by email. Use that one instead.\n" + "═".repeat(50) + "\n\n";
    const blob = new Blob([warning + rawText], { type:"text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${(subjectName||recipientName||"interview").replace(/\s+/g,"_")}_RAW_BACKUP.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Do NOT clear cleanedTranscript
  };

  const downloadMedia = (blob, type) => {
    const ext = type === "video" ? "webm" : "webm";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(subjectName||recipientName||"interview").replace(/\s+/g,"_")}_Life_Story_Magic.${ext}`;
    a.click();
  };

  const sendResultEmail = () => {
    const to = mode === "send_interview" ? interviewerEmail : emailInput;
    const ccSelf = mode === "send_interview" && emailInput ? emailInput : "";
    const ccLSM = ccConsent ? "questions@lifestorymagic.com" : "";
    const ccExtra = additionalRecipients ? additionalRecipients.split(",").map(e=>e.trim()).filter(Boolean).join(",") : "";
    const ccList = [ccSelf, ccExtra, ccLSM].filter(Boolean).join(",");
    const text = cleanedTranscript || buildTranscript() || transcript.map(e => (e.question ? e.question + "\n\n" : "") + (e.answer || "")).join("\n\n---\n\n");
    const sub = recipientName || subjectName || "Someone";
    const arr = interviewerName || "you";
    const appUrl = "https://app.lifestorymagic.com";
    const bookUrl = "https://pro.lifestorymagic.com";
    const intro = mode === "send_interview"
      ? "Hi " + arr + ",\n\n" + sub + " answered your " + (transcript.length === 1 ? "question" : "questions") + ":\n\n"
      : "";
    const emailFooter = "\n\n---\nLife Story Magic — Please help us keep this free! https://app.lifestorymagic.com/?support=1";
    const outro = emailFooter;
    const subject = encodeURIComponent(sub + " answered " + (mode === "send_interview" ? arr + "\'s" : "your") + " questions");
    const body = encodeURIComponent(intro + text + outro);
    const ccParam = ccList ? "&cc=" + encodeURIComponent(ccList) : "";
    const mailtoLink = "mailto:" + to + "?subject=" + subject + ccParam + "&body=" + body;
    const a = document.createElement("a");
    a.href = mailtoLink;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setEmailSent(true);
  };

  const shortenURL = async (url) => url;

  const generateShareEmail = async () => {
    if (marketingConsent && interviewerEmail.trim()) {
      fetch("/.netlify/functions/subscribe", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email: interviewerEmail.trim() }) }).catch(()=>{});
    }
    // Save User 1 details to localStorage
    if (interviewerName.trim()) localStorage.setItem('lsm_u1_name', interviewerName.trim());
    if (interviewerEmail.trim()) localStorage.setItem('lsm_u1_email', interviewerEmail.trim());
    if (recipientName.trim()) {
      localStorage.setItem('lsm_last_recipient', recipientName.trim());
      const hist = JSON.parse(localStorage.getItem('lsm_recipient_history') || '[]');
      if (!hist.includes(recipientName.trim())) {
        hist.unshift(recipientName.trim());
        localStorage.setItem('lsm_recipient_history', JSON.stringify(hist.slice(0, 20)));
      }
    }
    const fromParam = interviewerEmail ? `&from=${encodeURIComponent(interviewerEmail)}` : "";
    const fromNameParam = interviewerName ? `&fromname=${encodeURIComponent(interviewerName)}` : "";
    const recipientNameParam = recipientName ? `&recipientname=${encodeURIComponent(recipientName)}` : "";
    const questionText = customQuestionText || (selectedModule?.questions?.[0]?.text || "");
    const longUrl = `${window.location.origin}${window.location.pathname}?${fromParam ? fromParam.slice(1)+"&" : ""}${fromNameParam ? fromNameParam.slice(1)+"&" : ""}${recipientNameParam ? recipientNameParam.slice(1)+"&" : ""}q=${encodeURIComponent(questionText)}`;
    const url = await shortenURL(longUrl);
    const subject = encodeURIComponent(`${recipientName} — can I ask you something?`);
    const body = encodeURIComponent(
`Hi ${recipientName},

I'm using the free Life Story Magic app to discover, save, and share important stories.

Can I ask you a question? Click the link below and answer by speaking or typing.

${url}

Life Story Magic will prepare your answer in a ready-to-send email for you to review and send back.

Thank you!
${interviewerName}

---
Life Story Magic — Please help us keep this free! https://app.lifestorymagic.com/?support=1`
    );
    const a = document.createElement("a");
    a.href = `mailto:?subject=${subject}&body=${body}`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const generateShareSMS = async () => {
    if (marketingConsent && interviewerEmail.trim()) {
      fetch("/.netlify/functions/subscribe", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email: interviewerEmail.trim() }) }).catch(()=>{});
    }
    if (interviewerName.trim()) localStorage.setItem('lsm_u1_name', interviewerName.trim());
    if (interviewerEmail.trim()) localStorage.setItem('lsm_u1_email', interviewerEmail.trim());
    const fromParam = interviewerEmail ? `&from=${encodeURIComponent(interviewerEmail)}` : "";
    const fromNameParam = interviewerName ? `&fromname=${encodeURIComponent(interviewerName)}` : "";
    const recipientNameParam = recipientName ? `&recipientname=${encodeURIComponent(recipientName)}` : "";
    const questionText = customQuestionText || (selectedModule?.questions?.[0]?.text || "");
    const longUrl = `${window.location.origin}${window.location.pathname}?${fromParam ? fromParam.slice(1)+"&" : ""}${fromNameParam ? fromNameParam.slice(1)+"&" : ""}${recipientNameParam ? recipientNameParam.slice(1)+"&" : ""}q=${encodeURIComponent(questionText)}`;
    const url = await shortenURL(longUrl);
    const body = encodeURIComponent(`Hi ${recipientName || "there"}. Can you click here and answer a question for me? You can speak or type your answer.\n\n${url}`);
    const a = document.createElement("a");
    a.href = `sms:?body=${body}`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const generateShareWhatsApp = async () => {
    if (marketingConsent && interviewerEmail.trim()) {
      fetch("/.netlify/functions/subscribe", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email: interviewerEmail.trim() }) }).catch(()=>{});
    }
    if (interviewerName.trim()) localStorage.setItem('lsm_u1_name', interviewerName.trim());
    if (interviewerEmail.trim()) localStorage.setItem('lsm_u1_email', interviewerEmail.trim());
    const fromParam = interviewerEmail ? `&from=${encodeURIComponent(interviewerEmail)}` : "";
    const fromNameParam = interviewerName ? `&fromname=${encodeURIComponent(interviewerName)}` : "";
    const recipientNameParam = recipientName ? `&recipientname=${encodeURIComponent(recipientName)}` : "";
    const questionText = customQuestionText || (selectedModule?.questions?.[0]?.text || "");
    const longUrl = `${window.location.origin}${window.location.pathname}?${fromParam ? fromParam.slice(1)+"&" : ""}${fromNameParam ? fromNameParam.slice(1)+"&" : ""}${recipientNameParam ? recipientNameParam.slice(1)+"&" : ""}q=${encodeURIComponent(questionText)}`;
    const url = await shortenURL(longUrl);
    const text = encodeURIComponent(`Hi ${recipientName || "there"}. Can you click here and answer a question for me? You can speak or type your answer.\n\n${url}`);
    const a = document.createElement("a");
    a.href = `https://wa.me/?text=${text}`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Last-words live display — shows only recent words, not the full raw stream


  const progressPct = isCustomMode ? (hasAnswered ? 100 : 0) : (questions.length ? ((qIndex) / questions.length) * 100 : 0);

  const NEWSLETTER_URL = "https://lifestorymagic.com"; // replace with actual signup URL

  const FAQ_ITEMS = [
    {
      q: "What is Life Story Magic?",
      a: "The mission of Life Story Magic is to help people discover, save, and share their most important stories. The Life Story Magic Free Web App lets you ask questions and capture stories from the people who matter to you. You can speak or type questions, and they can speak or type their answers. We transcribe spoken responses and prepare messages for you to review and send. No account is required, and Life Story Magic does not store the recording or transcript."
    },
    {
      q: "How does the transcription work?",
      a: "We use your device's microphone to capture your speech, then send the audio to OpenAI's Whisper service which converts it to text. We then use Anthropic's Claude AI to clean up the transcript — adding punctuation, removing filler words — so the final result is readable and natural."
    },
    {
      q: "Is my family's information private?",
      a: "The transcript is sent by email — to the person who requested the interview, and optionally to you and others you choose. We do not store your answers on our servers. The only copy that exists after the interview ends is the one in your email. We take privacy seriously and will never sell or share your personal information. See our <a href='https://docs.google.com/document/d/1yTiM7VGVMMoQ08Ai0rRUrJDLFFipZHNuWFSWiyBilHQ/' target='_blank'>Privacy Policy</a> for full details."
    },
    {
      q: "Will my answers be used to train AI?",
      a: "Neither OpenAI nor Anthropic uses API data to train their models by default. Your audio and transcript are processed and discarded. OpenAI may retain audio briefly for abuse monitoring before deleting it. We ourselves store nothing."
    },
    {
      q: "What happens if the transcription isn't perfect?",
      a: "Speech-to-text technology has come a long way but it isn't perfect — especially with names, accents, or background noise. We automatically clean up the most common issues, but you may notice occasional errors. The recording captures what was actually said, so the meaning is always preserved even if a word or two is off. This is an early version of the app and transcription quality will improve over time."
    },
    {
      q: "Is this app free?",
      a: "Yes — the version you're using right now is completely free. Life Story Magic also offers professional video interviews conducted by experienced journalists. <a href='https://pro.lifestorymagic.com' target='_blank' rel='noreferrer'>Use code FREEAPP for 20% off your first session.</a>"
    },
    {
      q: "How do I get the best transcription quality?",
      a: "Find a quiet room with no TV, music, or background noise. Speak clearly and at a natural pace. Let one person finish before the other starts. Keep the app open and in the foreground while recording."
    },
    {
      q: "Why can't I just ask the questions myself?",
      a: "You can! You should! But have you? Life Story Magic gives you a simple, free way to ask the question, record the answer, and make sure it actually gets saved and shared — rather than staying on your to-do list forever. And when you do ask in person, does the answer get preserved anywhere? Now it does."
    },
    {
      q: "Is there really no account or login required?",
      a: "Really. Everything is delivered by email — your inbox is your storage. We save nothing on our servers. No passwords to remember, no account to create, no data to worry about. The transcript lives in your email, where it's easy to save, forward, and share with your whole family."
    },
    {
      q: "Who built this?",
      a: "Life Story Magic was founded by Bill Murphy Jr., an award-winning journalist and author. Bill trained under Bob Woodward at The Washington Post, has written for The New York Times, The Atlantic, Inc., and Fast Company, and is the author of several books including <em>The Intelligent Entrepreneur</em> and <em>In a Time of War</em>. He started Life Story Magic because he believes everyone deserves to have their stories captured and preserved. <a href='https://www.lifestorymagic.com' target='_blank' rel='noreferrer'>Learn more at lifestorymagic.com →</a>"
    },
  ];

  const sendFeedback = () => {
    const emoji = feedbackType === "positive" ? "👍" : "👎";
    const subject = encodeURIComponent(`${emoji} Life Story Magic Feedback`);
    const body = encodeURIComponent(`Rating: ${feedbackType === "positive" ? "Positive 👍" : "Negative 👎"}\n\nComments:\n${feedbackText || "(none)"}\n\nModule: ${selectedModule?.label || "—"}\nMode: ${mode || "—"}`);
    const a = document.createElement("a");
    a.href = `mailto:feedback@lifestorymagic.com?subject=${subject}&body=${body}`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setFeedbackSent(true);
  };

  const FeedbackModal = () => feedbackOpen ? (
    <div className="feedback-overlay" onClick={e=>{ if(e.target===e.currentTarget) setFeedbackOpen(false); }}>
      <div className="feedback-modal">
        {!feedbackSent ? (
          <>
            <div className="feedback-title">How is Life Story Magic working for you?</div>
            <div className="feedback-sub">This is an private beta — your feedback helps us build something genuinely useful.</div>
            <div className="feedback-type-row">
              <button className={`feedback-type-btn ${feedbackType==="positive"?"selected":""}`} onClick={()=>setFeedbackType("positive")}>👍<br/><span style={{fontSize:"0.7rem",fontFamily:"Lexend,sans-serif",fontWeight:"600",color:"var(--text-2)"}}>Working well</span></button>
              <button className={`feedback-type-btn ${feedbackType==="negative"?"selected":""}`} onClick={()=>setFeedbackType("negative")}>👎<br/><span style={{fontSize:"0.7rem",fontFamily:"Lexend,sans-serif",fontWeight:"600",color:"var(--text-2)"}}>Something's wrong</span></button>
            </div>
            {feedbackType === "positive" && (
              <div className="feedback-positive-box">
                That's great to hear! If you have a moment, a testimonial or a <a href="https://buymeacoffee.com" target="_blank" rel="noreferrer">small contribution</a> helps us keep building. And if you share Life Story Magic with someone, that means the world to us.
              </div>
            )}
            <textarea
              className="input"
              style={{minHeight:"80px",resize:"vertical",fontSize:"0.88rem",marginBottom:"12px"}}
              placeholder={feedbackType==="negative" ? "Tell us what went wrong — we read every message." : "Anything else you'd like to share?"}
              value={feedbackText}
              onChange={e=>setFeedbackText(e.target.value)}
            />
            <div style={{display:"flex",gap:"10px"}}>
              <button className="btn btn-primary" style={{flex:1}} onClick={sendFeedback} disabled={!feedbackType}>Send feedback</button>
              <button className="btn btn-ghost" onClick={()=>setFeedbackOpen(false)}>Cancel</button>
            </div>
          </>
        ) : (
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontSize:"2rem",marginBottom:"12px"}}>✓</div>
            <div style={{fontWeight:"800",fontSize:"1.1rem",color:"var(--text)",marginBottom:"8px"}}>Thank you for the feedback.</div>
            <div style={{fontSize:"0.84rem",color:"var(--text-2)",marginBottom:"20px",lineHeight:"1.6"}}>We read every message and use it to make Life Story Magic better.</div>
            <button className="btn btn-secondary" onClick={()=>{ setFeedbackOpen(false); setFeedbackSent(false); setFeedbackType(null); setFeedbackText(""); }}>Close</button>
          </div>
        )}
      </div>
    </div>
  ) : null;

  const FAQ = ({title}) => (
    <div className="faq-section">
      <div className="faq-title">{title || "Frequently asked questions"}</div>
      {FAQ_ITEMS.map((item, i) => (
        <div key={i} className="faq-item">
          <button className="faq-q" onClick={()=>setFaqOpen(faqOpen===i?null:i)}>
            <span>{item.q}</span>
            <span className={`faq-chevron ${faqOpen===i?"open":""}`}>▼</span>
          </button>
          {faqOpen===i && <div className="faq-a" dangerouslySetInnerHTML={{__html:item.a}} />}
        </div>
      ))}
      <div style={{marginTop:"20px",paddingTop:"16px",borderTop:"1px solid var(--border)",fontSize:"0.82rem",color:"var(--text-3)",lineHeight:"1.8"}}>
        <span style={{fontSize:"0.88rem"}}>Coming soon: </span>
        <span style={{fontFamily:"system-ui,sans-serif"}}>español · 普通话 · Português · français · हिन्दी · 한국어 · Tiếng Việt · العربية</span>
        <br/>
        <a href={NEWSLETTER_URL} target="_blank" rel="noreferrer" style={{color:"var(--purple)",fontWeight:"600",textDecoration:"none"}}>
          Sign up for our emails
        </a> to be notified when your language is ready.
      </div>
    </div>
  );

  const EXAMPLE_QUESTIONS = [
    "Can you tell me the family recipe for lasagna?",
    "Where did you go on your honeymoon?",
    "Can you tell me about the jobs you've had over the years?",
    "What was it like growing up in your house?",
    "What do you remember most about your own parents?",
    "Can you tell me about a time in your life that really changed you?",
    "What is your earliest memory?",
    "Can you tell me about the day I was born?",
    "What was your proudest moment?",
    "Who was the most important person in your life growing up?",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCycleFade(false);
      setTimeout(() => {
        setCycleIdx(i => (i + 1) % EXAMPLE_QUESTIONS.length);
        setCycleFade(true);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        :root {
          --purple: #6432C4;
          --purple-dark: #4e27a0;
          --purple-hover: #7a44d4;
          --purple-light: #f0eafa;
          --purple-mid: rgba(100,50,196,0.15);
          --bg: #f7f6fb;
          --bg2: #ffffff;
          --border: rgba(100,50,196,0.13);
          --text: #1a1626;
          --text-2: #4a4360;
          --text-3: #9890b0;
          --red: #E53935;
          --red-dim: rgba(229,57,53,0.1);
          --green: #16A34A;
          --green-dim: rgba(22,163,74,0.1);
        }
        html,body { height:100%; background:var(--bg); }
        body { font-family:'Lexend',sans-serif; color:var(--text); -webkit-font-smoothing:antialiased; }

        /* NAV */
        .nav {
          height:60px; background:var(--purple);
          display:flex; align-items:center; justify-content:space-between;
          padding:0 40px; position:sticky; top:0; z-index:100;
          box-shadow: 0 2px 12px rgba(100,50,196,0.25);
        }
        .nav-logo { display:flex; align-items:center; gap:12px; }
        .nav-logo img { height:36px; width:auto; }
        .nav-logo span { font-size:1rem; font-weight:800; color:#fff; letter-spacing:-0.01em; }
        .nav-right { font-size:0.75rem; font-weight:600; color:rgba(255,255,255,0.75); cursor:pointer; }
        .nav-right:hover { color:#fff; }

        /* PAGE */
        .page { max-width:820px; margin:0 auto; padding:48px 40px 80px; }
        .page-narrow { max-width:600px; }

        /* HERO */
        .hero { text-align:center; margin-bottom:56px; }
        .hero-eyebrow { font-size:0.68rem; font-weight:700; letter-spacing:0.16em; text-transform:uppercase; color:var(--purple); margin-bottom:16px; }
        .hero-title { font-size:2.8rem; font-weight:800; letter-spacing:-0.04em; color:var(--text); line-height:1.1; margin-bottom:16px; }
        .hero-sub { font-size:1.05rem; color:var(--text-2); line-height:1.7; font-weight:400; max-width:520px; margin:0 auto; }

        /* MODE CARDS */
        .mode-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-bottom:40px; }
        .mode-card {
          background:var(--bg2); border:1.5px solid var(--border); border-radius:16px;
          padding:32px 28px; cursor:pointer; transition:border-color 0.15s, box-shadow 0.15s;
          text-align:left;
        }
        .mode-card:hover { border-color:var(--purple); box-shadow:0 4px 20px rgba(100,50,196,0.12); }
        .mode-card-icon { font-size:2rem; margin-bottom:16px; }
        .mode-card-title { font-size:1.1rem; font-weight:800; color:var(--text); margin-bottom:8px; letter-spacing:-0.02em; }
        .mode-card-desc { font-size:0.87rem; color:var(--text-2); line-height:1.65; font-weight:400; }

        /* FORM */
        .form-section { margin-bottom:32px; }
        .section-eyebrow { font-size:0.65rem; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; color:var(--purple); margin-bottom:20px; }
        .screen-title { font-size:1.9rem; font-weight:800; letter-spacing:-0.03em; color:var(--text); margin-bottom:8px; line-height:1.2; }
        .screen-sub { font-size:0.92rem; color:var(--text-2); line-height:1.7; font-weight:400; margin-bottom:32px; }
        .field { margin-bottom:18px; }
        .field-label { display:block; font-size:0.67rem; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--text-3); margin-bottom:7px; }
        .input { width:100%; padding:13px 15px; background:var(--bg2); border:1.5px solid var(--border); border-radius:10px; color:var(--text); font-family:'Lexend',sans-serif; font-size:0.95rem; font-weight:400; outline:none; transition:border-color 0.15s; }
        .input:focus { border-color:var(--purple); }
        .input::placeholder { color:var(--text-3); }
        .field-row { display:grid; grid-template-columns:1fr 1fr; gap:16px; }

        /* MODULE PICKER */
        .module-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:12px; margin-bottom:28px; }
        .module-btn {
          background:var(--bg2); border:1.5px solid var(--border); border-radius:12px;
          padding:16px 14px; text-align:left; cursor:pointer; transition:all 0.15s;
        }
        .module-btn:hover { border-color:var(--purple); }
        .module-btn.selected { border-color:var(--purple); background:var(--purple-light); }
        .module-btn-num { font-size:0.62rem; font-weight:700; letter-spacing:0.1em; color:var(--text-3); margin-bottom:5px; }
        .module-btn-name { font-size:0.88rem; font-weight:700; color:var(--text); margin-bottom:4px; }
        .module-btn-desc { font-size:0.75rem; color:var(--text-2); line-height:1.45; font-weight:400; }
        .module-btn.selected .module-btn-name { color:var(--purple); }

        .module-preview { background:var(--bg2); border:1.5px solid var(--border); border-radius:12px; padding:20px 24px; margin-bottom:28px; }
        .module-preview-title { font-size:0.65rem; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--text-3); margin-bottom:14px; }
        .module-q-list { list-style:none; display:flex; flex-direction:column; gap:10px; }
        .module-q-item { font-size:0.85rem; color:var(--text-2); line-height:1.5; font-weight:400; padding-left:20px; position:relative; }
        .module-q-item::before { content:attr(data-n); position:absolute; left:0; font-size:0.68rem; font-weight:700; color:var(--purple); }

        /* RECORDING CHOICE */
        .rec-choice { display:flex; gap:12px; margin-bottom:28px; }
        .rec-choice-btn {
          flex:1; padding:16px; background:var(--bg2); border:1.5px solid var(--border); border-radius:12px;
          cursor:pointer; text-align:center; transition:all 0.15s; font-family:'Lexend',sans-serif;
        }
        .rec-choice-btn:hover { border-color:var(--purple); }
        .rec-choice-btn.selected { border-color:var(--purple); background:var(--purple-light); }
        .rec-choice-icon { font-size:1.5rem; margin-bottom:6px; }
        .rec-choice-label { font-size:0.82rem; font-weight:700; color:var(--text); }
        .rec-choice-sub { font-size:0.72rem; color:var(--text-2); font-weight:400; margin-top:2px; }

        /* BUTTONS */
        .btn { font-family:'Lexend',sans-serif; font-weight:700; border:none; cursor:pointer; border-radius:10px; transition:all 0.15s; -webkit-appearance:none; }
        .btn-primary { background:var(--purple); color:#fff; padding:14px 28px; font-size:0.95rem; width:100%; border-radius:12px; }
        .btn-primary:hover { background:var(--purple-hover); }
        .btn-primary:disabled { opacity:0.35; cursor:not-allowed; }
        .btn-secondary { background:var(--bg2); color:var(--text-2); padding:13px 28px; font-size:0.92rem; width:100%; border:1.5px solid var(--border); border-radius:12px; }
        .btn-secondary:hover { border-color:var(--purple); color:var(--purple); }
        .btn-ghost { background:transparent; color:var(--text-3); padding:10px 16px; font-size:0.82rem; border:1.5px solid var(--border); }
        .btn-ghost:hover { color:var(--text-2); border-color:rgba(100,50,196,0.25); }
        .btn-danger { background:var(--red-dim); color:var(--red); padding:13px 28px; font-size:0.92rem; width:100%; border:1.5px solid rgba(229,57,53,0.2); border-radius:12px; }
        .btn-stack { display:flex; flex-direction:column; gap:10px; }

        /* INTERVIEW LAYOUT */
        .interview-layout { display:flex; flex-direction:column; gap:20px; }
        @media (min-width:700px) { .interview-layout { display:grid; grid-template-columns:260px 1fr; gap:32px; align-items:start; } }
        .interview-sidebar { display:none; }
        @media (min-width:700px) { .interview-sidebar { display:block; position:sticky; top:92px; } }
        .sidebar-module { font-size:0.65rem; font-weight:700; letter-spacing:0.12em; text-transform:uppercase; color:var(--purple); margin-bottom:10px; }
        .sidebar-progress { height:3px; background:var(--purple-mid); border-radius:2px; margin-bottom:20px; overflow:hidden; }
        .sidebar-progress-fill { height:100%; background:var(--purple); border-radius:2px; transition:width 0.4s; }
        .sidebar-q-list { display:flex; flex-direction:column; gap:4px; }
        .sidebar-q-item { padding:8px 10px; border-radius:8px; font-size:0.78rem; line-height:1.45; font-weight:400; color:var(--text-3); cursor:pointer; transition:all 0.15s; }
        .sidebar-q-item.current { background:var(--purple-light); color:var(--purple); font-weight:600; }
        .sidebar-q-item.answered { color:var(--text-2); }
        .sidebar-q-item.answered::after { content:' ✓'; color:var(--green); font-size:0.7rem; }

        /* RECORDING STATUS BAR */
        .rec-bar {
          background:var(--bg2); border:1.5px solid var(--border); border-radius:12px;
          padding:12px 16px; display:flex; align-items:center; gap:12px; margin-bottom:16px;
        }
        .rec-bar.live { border-color:var(--red); }
        .rec-dot { width:9px; height:9px; border-radius:50%; background:var(--text-3); flex-shrink:0; }
        .rec-dot.live { background:var(--red); box-shadow:0 0 0 3px var(--red-dim); animation:pulse 1.2s infinite; }
        @keyframes pulse { 0%,100%{box-shadow:0 0 0 3px var(--red-dim);} 50%{box-shadow:0 0 0 7px rgba(229,57,53,0.04);} }
        .rec-status-text { font-size:0.75rem; font-weight:600; color:var(--text-3); flex:1; }
        .rec-status-text.live { color:var(--red); }
        .rec-timer { font-size:0.75rem; font-weight:700; color:var(--red); font-variant-numeric:tabular-nums; }

        /* Q CARD */
        .q-card { background:var(--bg2); border:1.5px solid var(--border); border-radius:16px; padding:28px; margin-bottom:16px; position:relative; overflow:hidden; }
        .q-card::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:var(--purple); }
        .q-eyebrow { font-size:0.62rem; font-weight:700; letter-spacing:0.12em; text-transform:uppercase; color:var(--purple); margin-bottom:10px; }
        .q-text { font-size:1.2rem; font-weight:700; line-height:1.5; color:var(--text); }

        /* TRANSCRIPT ZONE */
        .transcript-zone { background:var(--bg2); border:1.5px solid var(--border); border-radius:12px; overflow:hidden; margin-bottom:16px; transition:border-color 0.2s; }
        .transcript-zone.live { border-color:var(--red); }
        .transcript-zone-body { padding:16px; min-height:100px; font-size:0.92rem; line-height:1.8; color:var(--text-2); font-weight:400; }
        .transcript-zone-body em { color:var(--text-3); font-style:italic; }
        .transcript-zone-body.live-mode { color:var(--text); }
        .transcript-zone-body p { margin-bottom:10px; }
        .transcript-zone-body p:last-child { margin-bottom:0; }

        /* VIDEO PREVIEW */
        .video-preview-wrap { position:relative; background:#000; border-radius:12px; overflow:hidden; margin-bottom:16px; aspect-ratio:16/9; display:flex; align-items:center; justify-content:center; }
        .video-preview { width:100%; height:100%; object-fit:cover; }
        .video-preview-placeholder { color:rgba(255,255,255,0.3); font-size:0.85rem; font-weight:500; }

        /* RECORD BUTTON */
        .record-btn-wrap { display:flex; align-items:center; justify-content:center; gap:32px; margin-bottom:16px; }
        .record-circle-btn {
          width:88px; height:88px; border-radius:50%; border:none; cursor:pointer;
          display:flex; align-items:center; justify-content:center;
          background:var(--red); transition:transform 0.1s, box-shadow 0.15s;
          box-shadow:0 4px 24px rgba(229,57,53,0.45), 0 0 0 0 rgba(229,57,53,0.3);
          animation:readyPulse 2s ease-in-out infinite;
          -webkit-appearance:none;
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity:0.4; }
          40% { transform: scale(1.2); opacity:1; }
        }
        @keyframes readyPulse {
          0%,100% { box-shadow:0 4px 24px rgba(229,57,53,0.45), 0 0 0 0 rgba(229,57,53,0.3); }
          50% { box-shadow:0 4px 24px rgba(229,57,53,0.45), 0 0 0 12px rgba(229,57,53,0); }
        }
        .record-circle-btn:active { transform:scale(0.94); animation:none; }
        .record-circle-btn.recording { background:#fff; border:3px solid var(--red); box-shadow:0 4px 20px rgba(229,57,53,0.2); animation:none; }
        .record-circle-inner { font-size:28px; line-height:1; }
        .record-circle-inner.recording { width:24px; height:24px; border-radius:6px; background:var(--red); font-size:0; }
        .record-btn-label { font-size:0.72rem; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; color:var(--text-3); margin-top:8px; display:block; text-align:center; }
        .record-btn-label.live { color:var(--red); }
        .record-btn-col { display:flex; flex-direction:column; align-items:center; }

        .side-action-btn { padding:12px 20px; font-size:0.82rem; }

        /* TRANSCRIPT SCREEN */
        .t-header { margin-bottom:28px; }
        .t-subject { font-size:2rem; font-weight:800; letter-spacing:-0.03em; color:var(--text); margin-bottom:6px; }
        .t-meta { font-size:0.82rem; color:var(--text-3); font-weight:500; }
        .t-divider { height:1px; background:var(--border); margin:24px 0; }
        .t-entry { padding:20px 0; border-bottom:1px solid var(--border); }
        .t-entry:last-child { border-bottom:none; }
        .t-timestamp { font-size:0.62rem; font-weight:700; color:var(--purple); letter-spacing:0.1em; margin-bottom:6px; }
        .t-q { font-size:0.88rem; font-weight:700; color:var(--text-2); margin-bottom:10px; line-height:1.5; }
        .t-a { font-size:0.92rem; color:var(--text); line-height:1.8; white-space:pre-wrap; font-weight:400; }
        .t-empty { text-align:center; color:var(--text-3); padding:40px 0; font-size:0.9rem; }

        /* DOWNLOADS */
        .download-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:20px; }
        .download-card { background:var(--bg2); border:1.5px solid var(--border); border-radius:12px; padding:20px; }
        .download-card-title { font-size:0.8rem; font-weight:700; color:var(--text); margin-bottom:4px; }
        .download-card-sub { font-size:0.75rem; color:var(--text-3); margin-bottom:14px; font-weight:400; }

        .email-gate { background:var(--purple-light); border:1.5px solid var(--border); border-radius:12px; padding:20px; margin-bottom:20px; }
        .email-gate-title { font-size:0.85rem; font-weight:700; color:var(--text); margin-bottom:12px; }
        .email-row { display:flex; gap:10px; }
        .email-row .input { flex:1; }
        .btn-send { padding:12px 20px; background:var(--purple); color:#fff; border:none; border-radius:10px; font-family:'Lexend',sans-serif; font-weight:700; font-size:0.85rem; cursor:pointer; white-space:nowrap; }
        .checkbox-row { display:flex; align-items:flex-start; gap:10px; margin-top:10px; cursor:pointer; }
        .checkbox-row input { margin-top:3px; accent-color:var(--purple); flex-shrink:0; }
        .checkbox-label { font-size:0.8rem; color:var(--text-2); line-height:1.55; font-weight:400; }
        .consent-text { font-size:0.74rem; color:var(--text-3); line-height:1.6; margin-top:8px; font-weight:400; }
        .consent-text a { color:var(--purple); text-decoration:none; }
        .email-sent { background:var(--green-dim); border:1.5px solid rgba(22,163,74,0.2); border-radius:10px; padding:12px 16px; font-size:0.84rem; color:var(--green); font-weight:700; margin-bottom:16px; }

        /* PROMO */
        .promo-box { background:var(--bg2); border:1.5px solid var(--border); border-radius:16px; padding:28px; margin-top:32px; }
        .promo-eyebrow { font-size:0.65rem; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; color:var(--purple); margin-bottom:10px; }
        .promo-title { font-size:1.2rem; font-weight:800; color:var(--text); margin-bottom:10px; letter-spacing:-0.02em; }
        .promo-body { font-size:0.88rem; color:var(--text-2); line-height:1.7; margin-bottom:20px; font-weight:400; }
        .promo-code-chip { display:inline-block; background:var(--purple-light); border-radius:8px; padding:8px 16px; font-size:1.1rem; font-weight:800; color:var(--purple); letter-spacing:0.06em; margin-bottom:8px; }
        .promo-code-sub { font-size:0.78rem; color:var(--text-3); margin-bottom:20px; font-weight:400; }

        /* BEST PRACTICES */
        .bp-box { background:var(--bg2); border:1.5px solid var(--border); border-radius:12px; padding:20px 24px; margin-bottom:28px; }
        .bp-title { font-size:0.65rem; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--text-3); margin-bottom:12px; }
        .bp-list { list-style:none; display:flex; flex-direction:column; gap:8px; }
        .bp-list li { font-size:0.83rem; color:var(--text-2); line-height:1.5; padding-left:16px; position:relative; font-weight:400; }
        .bp-list li::before { content:'·'; position:absolute; left:0; color:var(--purple); font-weight:700; }

        .fade { animation:fadeUp 0.25s ease both; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(8px);} to{opacity:1;transform:translateY(0);} }
        .divider { height:1px; background:var(--border); margin:24px 0; }
        .back-link { font-size:0.8rem; font-weight:600; color:var(--text-3); cursor:pointer; display:inline-flex; align-items:center; gap:6px; margin-bottom:28px; }
        .back-link:hover { color:var(--purple); }

        .info-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.55); z-index:300; display:flex; align-items:center; justify-content:center; padding:16px; }
        .info-sheet { background:var(--bg2); border-radius:16px; padding:20px; max-width:480px; width:100%; max-height:80vh; overflow-y:auto; }
        .info-close { float:right; background:none; border:none; cursor:pointer; font-size:1.2rem; color:var(--text-3); font-family:'Lexend',sans-serif; margin-top:-4px; }

        .ideas-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.45); z-index:300; display:flex; align-items:center; justify-content:center; padding:20px; }
        .ideas-modal { background:var(--bg2); border-radius:16px; padding:28px; max-width:480px; width:100%; max-height:80vh; overflow-y:auto; }
        .ideas-title { font-size:1.1rem; font-weight:800; color:var(--text); margin-bottom:6px; letter-spacing:-0.02em; }
        .ideas-sub { font-size:0.84rem; color:var(--text-2); margin-bottom:20px; line-height:1.6; font-weight:400; }
        .ideas-chip { display:block; width:100%; text-align:left; background:var(--bg); border:1.5px solid var(--border); border-radius:10px; padding:12px 16px; margin-bottom:8px; font-family:'Lexend',sans-serif; font-size:0.88rem; color:var(--text); font-weight:500; cursor:pointer; transition:border-color 0.15s; line-height:1.5; }
        .ideas-chip:hover { border-color:var(--purple); color:var(--purple); }
        @keyframes cycleFadeIn { from{opacity:0;transform:translateY(4px);} to{opacity:1;transform:translateY(0);} }

        .faq-section { margin-top:32px; border-top:1px solid var(--border); padding-top:24px; }
        .faq-title { font-size:0.65rem; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; color:var(--text-3); margin-bottom:12px; display:block; }
        .faq-item { border-bottom:1px solid var(--border); display:block; }
        .faq-q { width:100%; padding:13px 0; text-align:left; background:transparent; border:none; cursor:pointer; display:flex; justify-content:space-between; align-items:flex-start; font-family:'Lexend',sans-serif; font-size:0.85rem; font-weight:600; color:var(--text); line-height:1.45; gap:8px; }
        .faq-chevron { font-size:0.65rem; color:var(--text-3); transition:transform 0.2s; flex-shrink:0; margin-top:3px; }
        .faq-chevron.open { transform:rotate(180deg); }
        .faq-a { font-size:0.82rem; color:var(--text-2); line-height:1.7; padding-bottom:12px; font-weight:400; display:block; }
        .faq-a a { color:var(--purple); text-decoration:none; }


        .feedback-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.4); z-index:300; display:flex; align-items:center; justify-content:center; padding:20px; }
        .feedback-modal { background:var(--bg2); border-radius:16px; padding:24px; max-width:440px; width:100%; }
        .feedback-title { font-size:1.05rem; font-weight:800; color:var(--text); margin-bottom:6px; letter-spacing:-0.02em; }
        .feedback-sub { font-size:0.82rem; color:var(--text-2); margin-bottom:16px; line-height:1.6; font-weight:400; }
        .feedback-type-row { display:flex; gap:10px; margin-bottom:14px; }
        .feedback-type-btn { flex:1; padding:12px; border-radius:10px; border:1.5px solid var(--border); background:var(--bg2); cursor:pointer; font-family:'Lexend',sans-serif; font-size:1.1rem; text-align:center; transition:all 0.15s; }
        .feedback-type-btn.selected { border-color:var(--purple); background:var(--purple-light); }
        .feedback-positive-box { background:var(--green-dim); border:1px solid rgba(22,163,74,0.2); border-radius:10px; padding:12px; margin-bottom:14px; font-size:0.82rem; color:var(--green); line-height:1.6; font-weight:500; }
        .feedback-positive-box a { color:var(--green); font-weight:700; }
      `}</style>

      <div>
        {/* NAV — always shown */}
        <nav className="nav">
          <div className="nav-logo">
            <a href="https://www.lifestorymagic.com" target="_blank" rel="noreferrer" style={{color:"#fff",fontWeight:"800",fontSize:"0.95rem",letterSpacing:"-0.01em",textDecoration:"none"}}>
              Life Story Magic
            </a>
            <button onClick={()=>setAlphaOpen(true)} style={{marginLeft:"8px",background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:"20px",cursor:"pointer",fontFamily:"'Lexend',sans-serif",fontWeight:"700",fontSize:"0.6rem",color:"rgba(255,255,255,0.8)",letterSpacing:"0.08em",textTransform:"uppercase",padding:"2px 8px"}}>Private Beta</button>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
            {(screen==="interview" || screen==="finishing" || screen==="transcript") && (
              <span className="nav-right" onClick={()=>setScreen("transcript")}>
                {transcript.length} captured →
              </span>
            )}
            <button onClick={()=>setHelpOpen(true)} style={{background:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:"6px",cursor:"pointer",fontFamily:"'Lexend',sans-serif",fontWeight:"800",fontSize:"0.72rem",color:"#fff",padding:"4px 10px"}}>Help keep this free!</button>

          </div>
        </nav>


        {!isEventMode && screen==="home" && (
          <div className="page fade" style={{paddingTop:"24px",paddingBottom:"40px",background:"linear-gradient(180deg, rgba(100,50,196,0.04) 0%, transparent 300px)"}}>

            {/* Minimal header */}
            <div style={{marginBottom:"20px"}}>
              <h1 style={{fontSize:"1.9rem",fontWeight:"900",letterSpacing:"-0.04em",color:"var(--text)",lineHeight:"1.15",marginBottom:"8px"}}>Discover, save, and share your stories.</h1>
              <p style={{fontSize:"0.82rem",fontWeight:"600",color:"var(--text-2)",marginBottom:"6px"}}>Free to use. No account required.</p>
            <button onClick={()=>setInfoOpen(true)} style={{background:"none",border:"none",cursor:"pointer",fontSize:"0.82rem",color:"var(--purple)",fontFamily:"'Lexend',sans-serif",fontWeight:"600",padding:"0",textDecoration:"underline"}}>
                What is this and how does it work? →
              </button>
            </div>

            {/* Visual flow — compact horizontal */}
            {!showTypeForm && !voiceComplete && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"16px",padding:"10px 12px",background:"var(--bg2)",borderRadius:"12px",border:"1px solid var(--border)",overflow:"hidden"}}>
                {[
                  { icon:"🎙", label:"You ask" },
                  { icon:"→", label:"" },
                  { icon:"💬", label:"They answer" },
                  { icon:"→", label:"" },
                  { icon:"📩", label:"You receive" },

                ].map((step, i) => (
                  <div key={i} style={{textAlign:"center",flexShrink:0}}>
                    {step.label ? (
                      <>
                        <div style={{fontSize:"1rem"}}>{step.icon}</div>
                        <div style={{fontSize:"0.58rem",color:"var(--text-3)",fontWeight:"600",marginTop:"2px",whiteSpace:"nowrap"}}>{step.label}</div>
                      </>
                    ) : (
                      <div style={{fontSize:"0.7rem",color:"var(--border)",fontWeight:"400",paddingBottom:"14px"}}>›</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Single flow */}
            <div style={{background:"var(--bg2)",border:"1.5px solid var(--border)",borderRadius:"16px",padding:"20px",marginBottom:"20px",boxShadow:"0 4px 24px rgba(100,50,196,0.08)",borderTop:"3px solid var(--purple)"}}>


              {/* PHASE 1: Relationship + QSP flow */}
              {!showTypeForm && (
                <div style={{padding:"4px 0 8px"}}>

                  {/* Who dropdown */}
                  <div style={{marginBottom:"16px"}}>
                    <div style={{fontSize:"0.95rem",fontWeight:"800",color:"var(--text)",marginBottom:"4px",letterSpacing:"-0.01em"}}>Who are we asking?</div>
                    <select
                      className="input"
                      style={{fontSize:"1rem",cursor:"pointer"}}
                      value={selectedRelationship || ""}
                      onChange={e=>{
                        const val = e.target.value;
                        setSelectedRelationship(val);
                        if (val && val !== "Other") setRecipientName(val);
                        else setRecipientName("");
                      }}
                    >
                      <option value="">— Choose a person —</option>
                      <option value="Mom">Mom</option>
                      <option value="Dad">Dad</option>
                      <option value="Grandma">Grandma</option>
                      <option value="Grandpa">Grandpa</option>
                      <option value="Other">✏️ Other — I'll type their name</option>
                    </select>
                    {selectedRelationship === "Other" && (
                      <input
                        className="input"
                        style={{marginTop:"8px",fontSize:"1rem"}}
                        placeholder="Type their name"
                        value={recipientName}
                        onChange={e=>setRecipientName(e.target.value)}
                        autoFocus
                      />
                    )}
                  </div>

                  {/* QSP list — always visible */}
                  <div style={{marginBottom:"8px"}}>
                    <div style={{marginBottom:"10px"}}>
                      <div style={{fontSize:"0.95rem",fontWeight:"800",color:"var(--text)",marginBottom:"4px",letterSpacing:"-0.01em"}}>What do you want to ask them?</div>
                      <div style={{fontSize:"0.78rem",color:"var(--text-3)",lineHeight:"1.5"}}>Use these suggestions, or ask anything you want. <span style={{fontStyle:"italic"}}>(Early version — new question suggestions will be added regularly.)</span></div>
                    </div>
                    <select
                      className="input"
                      style={{fontSize:"1rem",cursor:"pointer",marginBottom:"0"}}
                      value={selectedModule?.id || ""}
                      onChange={e=>{
                        const val = e.target.value;
                        if (val === "custom") {
                          setShowTypeForm(true);
                        } else {
                          const m = ALL_MODULES.find(x=>x.id===val);
                          if (m) {
                            setSelectedModule(m);
                            setCustomQuestionText("");
                            setMode("send_interview");
                            setScreen("setup_send");
                          }
                        }
                      }}
                    >
                      <option value="">— Choose a question suggestion —</option>
                      <option value="custom">✏️ Ask my own question</option>
                      <option value="" disabled>──────────────</option>
                      {ALL_MODULES.map(m=>(
                        <option key={m.id} value={m.id}>{m.questions[0].text}</option>
                      ))}
                    </select>

                    <p style={{fontSize:"0.72rem",color:"var(--text-3)",marginTop:"8px",fontStyle:"italic",lineHeight:"1.5"}}>One question at a time for now — multi-question conversations are coming soon.</p>

                    {/* Sign up for updates — below dropdown */}
                    <div style={{marginTop:"10px"}}>
                      {!signupDone ? (
                        !signupOpen ? (
                          <button onClick={()=>setSignupOpen(true)} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"'Lexend',sans-serif",fontSize:"0.78rem",color:"var(--purple)",fontWeight:"600",padding:"0",textDecoration:"underline"}}>Sign up to be notified when new questions are added →</button>
                        ) : (
                          <div style={{display:"flex",flexDirection:"column",gap:"6px"}}>
                            <input
                              className="input"
                              type="email"
                              placeholder="your@email.com"
                              value={signupEmail}
                              onChange={e=>setSignupEmail(e.target.value)}
                              style={{width:"100%",fontSize:"0.88rem"}}
                              autoFocus
                            />
                            <button className="btn btn-primary" style={{width:"100%",fontSize:"0.85rem"}} onClick={async ()=>{
                              if (!signupEmail.trim()) return;
                              try { await fetch("/.netlify/functions/subscribe", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email: signupEmail.trim() }) }); } catch(e) {}
                              setSignupOpen(false);
                              setSignupEmail("");
                              setSignupDone(true);
                            }}>Subscribe →</button>
                            <button onClick={()=>setSignupOpen(false)} style={{background:"none",border:"none",cursor:"pointer",fontSize:"0.75rem",color:"var(--text-3)",fontFamily:"'Lexend',sans-serif",textAlign:"center",textDecoration:"underline"}}>Cancel</button>
                          </div>
                        )
                      ) : (
                        <div style={{fontSize:"0.78rem",color:"var(--green)",fontWeight:"600"}}>✓ You're subscribed!</div>
                      )}
                    </div>
                  </div>

                  {/* Voice recording state */}
                  {(isVoiceRecording || isGeneratingQuestion) && (
                    <div style={{textAlign:"center",padding:"16px 0"}}>
                      {isVoiceRecording && (
                        <>
                          <button className="record-circle-btn recording" onClick={stopVoiceAndGenerate} style={{margin:"0 auto 8px"}}>
                            <div className="record-circle-inner recording" />
                          </button>
                          <div style={{fontSize:"0.72rem",color:"var(--red)",fontWeight:"700",textTransform:"uppercase",letterSpacing:"0.06em"}}>
                            ● {formatTime(voiceElapsed)} — tap to stop
                          </div>
                        </>
                      )}
                      {isGeneratingQuestion && (
                        <>
                          <div style={{display:"flex",gap:"6px",justifyContent:"center",marginBottom:"10px"}}>
                            {[0,1,2,3,4].map(i=>(
                              <div key={i} style={{width:"8px",height:"8px",borderRadius:"50%",background:"var(--purple)",animation:`bounce 1.2s ease-in-out ${i*0.15}s infinite`}} />
                            ))}
                          </div>
                          <div style={{fontSize:"0.88rem",color:"var(--purple)",fontWeight:"700"}}>Figuring out your question…</div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* PHASE 2: Custom question form */}
              {showTypeForm && (
                <>
                  <button onClick={()=>{ setShowTypeForm(false); setCustomQuestionText(""); setVoiceComplete(false); setVoiceError(false); }} style={{background:"none",border:"none",cursor:"pointer",fontSize:"0.8rem",color:"var(--purple)",fontFamily:"'Lexend',sans-serif",fontWeight:"600",textDecoration:"underline",padding:"0 0 12px",display:"block"}}>
                    ← Back to question suggestions
                  </button>
                  {voiceComplete && (
                    <div style={{background:"var(--green-dim)",border:"1px solid rgba(22,163,74,0.2)",borderRadius:"10px",padding:"12px 16px",marginBottom:"16px",fontSize:"0.84rem",color:"var(--green)",fontWeight:"600"}}>
                      ✓ We figured out your question — review below, then click Next to draft and address your message.
                    </div>
                  )}
                  {voiceError && (
                    <div style={{background:"var(--red-dim)",border:"1px solid rgba(229,57,53,0.2)",borderRadius:"10px",padding:"12px 16px",marginBottom:"16px",fontSize:"0.84rem",color:"var(--red)",fontWeight:"600"}}>
                      Couldn't generate your question automatically — please type it below.
                    </div>
                  )}

                  {/* Voice recording states */}
                  {isVoiceRecording && (
                    <div style={{textAlign:"center",marginBottom:"16px",padding:"16px",background:"var(--bg)",borderRadius:"12px",border:"1.5px solid var(--red)"}}>
                      <button className="record-circle-btn recording" onClick={stopVoiceAndGenerate} style={{margin:"0 auto 8px"}}>
                        <div className="record-circle-inner recording" />
                      </button>
                      <div style={{fontSize:"0.72rem",color:"var(--red)",fontWeight:"700",textTransform:"uppercase",letterSpacing:"0.06em",marginTop:"6px"}}>● {formatTime(voiceElapsed)} — tap to stop</div>
                    </div>
                  )}
                  {isGeneratingQuestion && (
                    <div style={{textAlign:"center",marginBottom:"16px",padding:"16px"}}>
                      <div style={{display:"flex",gap:"6px",justifyContent:"center",marginBottom:"10px"}}>
                        {[0,1,2,3,4].map(i=>(<div key={i} style={{width:"8px",height:"8px",borderRadius:"50%",background:"var(--purple)",animation:`bounce 1.2s ease-in-out ${i*0.15}s infinite`}} />))}
                      </div>
                      <div style={{fontSize:"0.88rem",color:"var(--purple)",fontWeight:"700"}}>Figuring out your question…</div>
                    </div>
                  )}

                  <div className="field">
                    <div style={{fontSize:"1.3rem",fontWeight:"800",color:"var(--text)",letterSpacing:"-0.03em",marginBottom:"4px"}}>Who?</div>
                    <div style={{fontSize:"0.82rem",color:"var(--text-3)",marginBottom:"8px",fontWeight:"400"}}>Use their name as they'd recognize themselves — Mom, Grandma Rose, Jennifer, etc.</div>
                    <input className="input" style={{fontSize:"1rem"}} value={recipientName} onChange={e=>setRecipientName(e.target.value)} />
                  </div>
                  <div className="field" style={{marginBottom:"8px"}}>
                    <div style={{fontSize:"1.3rem",fontWeight:"800",color:"var(--text)",letterSpacing:"-0.03em",marginBottom:"4px"}}>What? <span style={{fontSize:"0.78rem",fontWeight:"400",color:"var(--text-3)",letterSpacing:"0"}}>{customQuestionText.length}/100</span></div>
                    <div style={{fontSize:"0.82rem",color:"var(--text-3)",marginBottom:"8px",fontWeight:"400"}}>Write your question here, edit if needed.</div>
                    <textarea className="input" style={{fontSize:"1rem",minHeight:"72px",resize:"vertical",lineHeight:"1.6"}} maxLength={100} value={customQuestionText} onChange={e=>setCustomQuestionText(e.target.value)} />
                  </div>

                  {/* Record button — below What, as secondary option */}
                  {!isVoiceRecording && !isGeneratingQuestion && (
                    <div style={{textAlign:"center",margin:"8px 0 16px"}}>
                      <span style={{fontSize:"0.72rem",color:"var(--text-3)"}}>— or speak it instead —</span>
                      <div style={{marginTop:"8px"}}>
                        <button className="record-circle-btn" onClick={startVoiceQuestion} style={{margin:"0 auto 6px",width:"48px",height:"48px"}}>
                          <span style={{fontSize:"20px",lineHeight:"1"}}>🎙</span>
                        </button>
                        <div style={{fontSize:"0.72rem",color:"var(--text-3)",fontStyle:"italic"}}>Tap to speak — we'll figure out the question</div>
                      </div>
                    </div>
                  )}

                  {!voiceComplete && (
                    <div style={{marginBottom:"16px"}}>
                      <button onClick={()=>setCustomQuestionText(EXAMPLE_QUESTIONS[cycleIdx].slice(0,100))} style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",cursor:"pointer",padding:"0 0 6px",fontFamily:"'Lexend',sans-serif",marginBottom:"4px"}}>
                        <span style={{fontSize:"0.84rem",color:"var(--purple)",fontStyle:"italic",fontWeight:"500",opacity:cycleFade?1:0,transition:"opacity 0.4s ease",display:"inline-block"}}>
                          e.g. "{EXAMPLE_QUESTIONS[cycleIdx]}"
                        </span>
                      </button>
                      <button onClick={()=>setIdeasOpen(true)} style={{background:"none",border:"none",cursor:"pointer",fontSize:"0.75rem",color:"var(--text-3)",fontFamily:"'Lexend',sans-serif",fontWeight:"600",textDecoration:"underline",padding:"0"}}>
                        See more ideas →
                      </button>
                    </div>
                  )}

                  {isMobile && !voiceComplete && (
                    <div style={{textAlign:"center",marginBottom:"16px",paddingBottom:"16px",borderBottom:"1px solid var(--border)"}}>
                      <button onClick={()=>setShowTypeForm(false)} style={{background:"none",border:"none",cursor:"pointer",fontSize:"0.8rem",color:"var(--purple)",fontFamily:"'Lexend',sans-serif",fontWeight:"600",textDecoration:"underline",padding:"0"}}>
                        🎙 Try voice instead — speak your question
                      </button>
                    </div>
                  )}
                  <div className="field">
                    <label style={{display:"block",fontSize:"0.88rem",fontWeight:"700",color:"var(--text)",marginBottom:"6px"}}>What's <em>your</em> name?</label>
                    <input id="interviewer-name-input" className="input" placeholder="Use the name they'd recognize — e.g. Mom, Sarah" value={interviewerName} onChange={e=>setInterviewerName(e.target.value)} />
                  </div>
                  <div className="field">
                    <label style={{display:"block",fontSize:"0.88rem",fontWeight:"700",color:"var(--text)",marginBottom:"4px"}}>What's <em>your</em> email address?</label>
                    <p style={{fontSize:"0.78rem",color:"var(--text-3)",marginBottom:"6px",fontWeight:"400"}}>Please double check — this is where your friend or family member will send their answer.</p>
                    <input id="interviewer-email-input" className="input" type="email" placeholder="you@example.com" value={interviewerEmail} onChange={e=>setInterviewerEmail(e.target.value)} />
                  </div>

                  <label className="checkbox-row" style={{marginBottom:"14px"}}>
                    <input type="checkbox" checked={marketingConsent} onChange={e=>setMarketingConsent(e.target.checked)} />
                    <span className="checkbox-label">✉️ You don't <strong>have</strong> to sign up for our emails. But I think you'll like them — and it helps us grow Life Story Magic and make it better for everyone. By leaving it checked and pressing Continue you agree to our <a href="https://docs.google.com/document/d/1yTiM7VGVMMoQ08Ai0rRUrJDLFFipZHNuWFSWiyBilHQ/" target="_blank" rel="noreferrer">Terms &amp; Privacy</a>.</span>
                  </label>
                </>
              )}

              {/* Continue button — validates, subscribes, shows send options */}
              {!showSendOptions && (
                <button
                  className="btn btn-primary"
                  style={{opacity: customQuestionText.trim() && interviewerName.trim() && interviewerEmail.trim() ? 1 : 0.4}}
                  onClick={async ()=>{
                    if (!customQuestionText.trim()) return;
                    if (!interviewerName.trim()) {
                      const el = document.getElementById("interviewer-name-input");
                      if (el) { el.focus(); el.style.borderColor="var(--red)"; setTimeout(()=>el.style.borderColor="",2000); }
                      return;
                    }
                    if (!interviewerEmail.trim()) {
                      const el = document.getElementById("interviewer-email-input");
                      if (el) { el.focus(); el.style.borderColor="var(--red)"; setTimeout(()=>el.style.borderColor="",2000); }
                      return;
                    }
                    // Save to localStorage
                    if (interviewerName.trim()) localStorage.setItem('lsm_u1_name', interviewerName.trim());
                    if (interviewerEmail.trim()) localStorage.setItem('lsm_u1_email', interviewerEmail.trim());
                    // Subscribe to Beehiiv if checked
                    if (marketingConsent && interviewerEmail.trim()) {
                      try { await fetch("/.netlify/functions/subscribe", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email: interviewerEmail.trim() }) }); } catch(e) {}
                    }
                    setShowSendOptions(true);
                  }}
                >
                  Continue →
                </button>
              )}

              {showSendOptions && (
                <div style={{marginTop:"12px",padding:"16px",background:"var(--bg)",border:"1.5px solid var(--purple)",borderRadius:"12px"}}>
                  <div style={{fontSize:"0.82rem",color:"var(--text-2)",lineHeight:"1.6",marginBottom:"12px"}}>We'll open a ready-to-send message. Choose the person from your contacts, review it, and press Send. Life Story Magic never receives their email address.</div>
                  <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                    <button className="btn btn-primary" onClick={async ()=>{ setMode("send_interview"); await generateShareEmail(); }}>
                      📧 Open email draft
                    </button>
                    <div style={{display:"flex",gap:"8px"}}>
                      <button className="btn btn-secondary" style={{flex:1}} onClick={async ()=>{ setMode("send_interview"); await generateShareSMS(); }}>
                        💬 Open text message
                      </button>
                      <button className="btn btn-secondary" style={{flex:1}} onClick={async ()=>{ setMode("send_interview"); await generateShareWhatsApp(); }}>
                        📱 Open WhatsApp
                      </button>
                    </div>
                    <button className="btn btn-secondary" style={{width:"100%"}} onClick={()=>{
                      const fromParam = interviewerEmail ? `&from=${encodeURIComponent(interviewerEmail)}` : "";
                      const fromNameParam = interviewerName ? `&fromname=${encodeURIComponent(interviewerName)}` : "";
                      const recipientNameParam = recipientName ? `&recipientname=${encodeURIComponent(recipientName)}` : "";
                      const qParam = customQuestionText ? `&q=${encodeURIComponent(customQuestionText)}` : "";
                      const questionText2 = customQuestionText || (selectedModule?.questions?.[0]?.text || "");
                      const url = `${window.location.origin}${window.location.pathname}?${fromParam ? fromParam.slice(1)+"&" : ""}${fromNameParam ? fromNameParam.slice(1)+"&" : ""}${recipientNameParam ? recipientNameParam.slice(1)+"&" : ""}q=${encodeURIComponent(questionText2)}`;
                      const bodyText = `Hi ${recipientName || "there"},\n\nI'm using the free Life Story Magic app to discover, save, and share important stories.\n\nCan I ask you a question? Click the link below and answer by speaking or typing.\n\n${url}\n\nLife Story Magic will prepare your answer in a ready-to-send email for you to review and send back.\n\nThank you!\n${interviewerName}`;
                      copyEmailText(bodyText);
                    }}>
                      📋 Copy draft to paste into any email
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ── SETUP: INTERVIEW ME ── */}
        {screen==="setup_me" && (
          <div className="page page-narrow fade">
            <span className="back-link" onClick={()=>setScreen("home")}>← Back</span>
            <div className="section-eyebrow">Interview Me</div>
            <h1 className="screen-title">Let's get started.</h1>
            <p className="screen-sub">Enter your name, choose a module, and decide whether you'd like to record audio or video alongside the transcript.</p>

            <div className="form-section">
              <div className="field">
                <label className="field-label">Your name</label>
                <input className="input" placeholder="e.g. Rose Murphy" value={subjectName} onChange={e=>setSubjectName(e.target.value)} />
              </div>
            </div>

            <div className="section-eyebrow" style={{marginBottom:"12px"}}>Ask one specific question — or choose a module</div>
            <div className="field" style={{marginBottom:"16px"}}>
              <label className="field-label">Your own question (optional)</label>
              <input
                className="input"
                placeholder='e.g. "Can you tell the story of the fishhook incident?"'
                value={customQuestionText}
                onChange={e=>setCustomQuestionText(e.target.value)}
              />
              {customQuestionText && <p style={{fontSize:"0.75rem",color:"var(--purple)",marginTop:"5px",fontWeight:"500"}}>✓ This question will be sent instead of a module.</p>}
            </div>
            {!customQuestionText && <div className="section-eyebrow" style={{marginBottom:"12px",opacity:0.6}}>Or choose a module</div>}
            <div className="module-grid" style={{gridTemplateColumns:"1fr 1fr 1fr", marginBottom:"16px", opacity:customQuestionText?0.35:1, pointerEvents:customQuestionText?"none":"auto"}}>
              {ALL_MODULES.map(m=>(
                <div key={m.id} className={`module-btn ${selectedModule.id===m.id?"selected":""}`} onClick={()=>setSelectedModule(m)}>
                  <div className="module-btn-num">{m.abbr}</div>
                  <div className="module-btn-name">{m.label}</div>
                  <div className="module-btn-desc">{m.description}</div>
                </div>
              ))}
            </div>

            <div className="module-preview">
              <div className="module-preview-title">Questions in this module</div>
              <ul className="module-q-list">
                {selectedModule.questions.map((q,i)=>(
                  <li key={q.id} className="module-q-item" data-n={`${i+1}.`}>{q.text}</li>
                ))}
              </ul>
            </div>



            <div className="bp-box">
              <div className="bp-title">For the best transcription</div>
              <ul className="bp-list">
                <li>Use Chrome on a desktop or laptop — best transcription quality</li>
                <li>Find a quiet room with no TV, music, or background noise</li>
                <li>Use a headset or external microphone if you have one</li>
                <li>Speak clearly and at a natural pace</li>
                <li>Keep this window open and in the foreground while recording</li>
              </ul>
            </div>

            <button className="btn btn-primary" disabled={!subjectName.trim()} onClick={async ()=>{
              if (recMode) await startMediaRecording(recMode);
              startInterview();
            }}>Begin Interview →</button>
          </div>
        )}

        {/* ── SETUP: SEND INTERVIEW ── */}
        {screen==="setup_send" && selectedModule && (
          <div className="page page-narrow fade">

            {/* Compact pack confirmation */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"var(--purple-light)",border:"1.5px solid rgba(100,50,196,0.2)",borderRadius:"12px",padding:"12px 16px",marginBottom:"20px"}}>
              <div>
                <div style={{fontSize:"0.65rem",fontWeight:"700",color:"var(--purple)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"2px"}}>Question Suggestion Pack</div>
                <div style={{fontSize:"0.95rem",fontWeight:"700",color:"var(--text)"}}>{selectedModule.label}</div>
                <div style={{fontSize:"0.75rem",color:"var(--text-3)",marginTop:"2px"}}>{selectedModule.questions.length} questions · {selectedModule.description}</div>
              </div>
              <button onClick={()=>setScreen("home")} style={{background:"none",border:"1px solid var(--purple)",borderRadius:"8px",cursor:"pointer",fontFamily:"'Lexend',sans-serif",fontSize:"0.72rem",color:"var(--purple)",fontWeight:"600",padding:"5px 10px",whiteSpace:"nowrap",marginLeft:"12px"}}>
                Change
              </button>
            </div>

            {/* Questions preview — collapsible */}
            <details style={{marginBottom:"20px"}}>
              <summary style={{fontSize:"0.78rem",fontWeight:"700",color:"var(--purple)",cursor:"pointer",marginBottom:"8px",listStyle:"none",display:"flex",alignItems:"center",gap:"6px"}}>
                <span>▸</span> See the questions they'll be asked
              </summary>
              <ol style={{margin:"8px 0 0",paddingLeft:"18px",display:"flex",flexDirection:"column",gap:"8px"}}>
                {selectedModule.questions.map((q,i)=>(
                  <li key={q.id} style={{fontSize:"0.85rem",color:"var(--text)",lineHeight:"1.5"}}>{q.text}</li>
                ))}
              </ol>
              <p style={{fontSize:"0.72rem",color:"var(--text-3)",marginTop:"8px",fontStyle:"italic"}}>🎙 They can speak or type their answer.</p>
            </details>

            {/* Who */}
            <div className="field">
              <label style={{display:"block",fontSize:"0.95rem",fontWeight:"700",color:"var(--text)",marginBottom:"4px"}}>Who are we asking?</label>
              <input className="input" style={{fontSize:"1rem"}} placeholder="e.g. Grandma Rose" value={recipientName} onChange={e=>setRecipientName(e.target.value)} />
            </div>

            {/* Details */}
            {recipientName.trim() && (
              <div style={{borderTop:"1px solid var(--border)",paddingTop:"20px",marginTop:"4px"}}>
                <div style={{fontSize:"0.72rem",fontWeight:"700",color:"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"12px"}}>Almost done:</div>
                <div className="field">
                  <label className="field-label">Your name</label>
                  <input id="interviewer-name-input" className="input" placeholder="Use the name they'd recognize — e.g. Mom, Sarah" value={interviewerName} onChange={e=>setInterviewerName(e.target.value)} />
                </div>
                <div className="field">
                  <label className="field-label">Your email <span style={{fontSize:"0.72rem",fontWeight:"400",color:"var(--text-3)"}}>— where their answer will be sent</span></label>
                  <input id="interviewer-email-input" className="input" type="email" placeholder="you@example.com" value={interviewerEmail} onChange={e=>setInterviewerEmail(e.target.value)} />
                </div>

                <label className="checkbox-row" style={{marginBottom:"14px"}}>
                  <input type="checkbox" checked={marketingConsent} onChange={e=>setMarketingConsent(e.target.checked)} />
                  <span className="checkbox-label">✉️ You don't <strong>have</strong> to sign up for our emails. But I think you'll like them — and it helps us grow Life Story Magic and make it better for everyone. By leaving it checked and pressing Continue you agree to our <a href="https://docs.google.com/document/d/1yTiM7VGVMMoQ08Ai0rRUrJDLFFipZHNuWFSWiyBilHQ/" target="_blank" rel="noreferrer">Terms &amp; Privacy</a>.</span>
                </label>

                {!interviewerName.trim() || !interviewerEmail.trim() ? (
                  <div style={{background:"var(--red-dim)",border:"1px solid rgba(229,57,53,0.2)",borderRadius:"8px",padding:"10px 14px",marginBottom:"12px",fontSize:"0.8rem",color:"var(--red)",fontWeight:"600"}}>
                    Please fill in your name and email above to continue.
                  </div>
                ) : !showSendOptions ? (
                  <button className="btn btn-primary" onClick={async ()=>{
                    if (interviewerName.trim()) localStorage.setItem('lsm_u1_name', interviewerName.trim());
                    if (interviewerEmail.trim()) localStorage.setItem('lsm_u1_email', interviewerEmail.trim());
                    if (marketingConsent && interviewerEmail.trim()) {
                      try { await fetch("/.netlify/functions/subscribe", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email: interviewerEmail.trim() }) }); } catch(e) {}
                    }
                    setShowSendOptions(true);
                  }}>Continue →</button>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                    <div style={{fontSize:"0.82rem",color:"var(--text-2)",lineHeight:"1.6",marginBottom:"4px"}}>We'll open a ready-to-send message. Choose {recipientName} from your contacts, review it, and press Send.</div>
                    <button className="btn btn-primary" onClick={async ()=>{ setMode("send_interview"); await generateShareEmail(); }}>
                      📧 Open email draft
                    </button>
                    <div style={{display:"flex",gap:"8px"}}>
                      <button className="btn btn-secondary" style={{flex:1}} onClick={async ()=>{ await generateShareSMS(); }}>
                        💬 Open text message
                      </button>
                      <button className="btn btn-secondary" style={{flex:1}} onClick={async ()=>{ await generateShareWhatsApp(); }}>
                        📱 Open WhatsApp
                      </button>
                    </div>
                    <button className="btn btn-secondary" style={{width:"100%"}} onClick={()=>{
                      const fromParam = interviewerEmail ? `&from=${encodeURIComponent(interviewerEmail)}` : "";
                      const fromNameParam = interviewerName ? `&fromname=${encodeURIComponent(interviewerName)}` : "";
                      const recipientNameParam = recipientName ? `&recipientname=${encodeURIComponent(recipientName)}` : "";
                      const questionText2 = customQuestionText || (selectedModule?.questions?.[0]?.text || "");
                      const url = `${window.location.origin}${window.location.pathname}?${fromParam ? fromParam.slice(1)+"&" : ""}${fromNameParam ? fromNameParam.slice(1)+"&" : ""}${recipientNameParam ? recipientNameParam.slice(1)+"&" : ""}q=${encodeURIComponent(questionText2)}`;
                      const bodyText = `Hi ${recipientName},\n\nI'm using the free Life Story Magic app to discover, save, and share important stories.\n\nCan I ask you a question? Click the link below and answer by speaking or typing.\n\n${url}\n\nLife Story Magic will prepare your answer in a ready-to-send email for you to review and send back.\n\nThank you!\n${interviewerName}`;
                      copyEmailText(bodyText);
                    }}>
                      📋 Copy message text instead
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── WELCOME (shared link recipients) ── */}
        {screen==="welcome" && (
          <div className="page page-narrow fade">
            {!localStorage.getItem('lsm_u2_visited') && (() => {
              localStorage.setItem('lsm_u2_visited', 'true');
              return null;
            })()}
            {!localStorage.getItem('lsm_u2_seen_welcome') && (
              <div style={{background:"var(--bg2)",border:"1.5px solid var(--border)",borderRadius:"14px",padding:"20px",marginBottom:"20px",position:"relative"}}>
                <button onClick={e=>{e.currentTarget.parentElement.style.display='none'; localStorage.setItem('lsm_u2_seen_welcome','true');}} style={{position:"absolute",top:"12px",right:"12px",background:"none",border:"none",cursor:"pointer",fontSize:"1.1rem",color:"var(--text-3)",fontFamily:"'Lexend',sans-serif",fontWeight:"700",lineHeight:"1"}}>✕</button>
                <div style={{fontSize:"1rem",fontWeight:"800",color:"var(--text)",marginBottom:"8px",letterSpacing:"-0.02em"}}>Someone has a question for you.</div>
                <p style={{fontSize:"0.88rem",color:"var(--text-2)",lineHeight:"1.7",marginBottom:"10px"}}>{interviewerName || "Someone"} is using the free Life Story Magic app to capture stories from people who matter to them. They want to hear yours.</p>
                <div style={{fontSize:"0.78rem",fontWeight:"700",color:"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"8px"}}>Here's what happens:</div>
                <ul style={{margin:"0 0 12px",paddingLeft:"18px",display:"flex",flexDirection:"column",gap:"6px"}}>
                  <li style={{fontSize:"0.85rem",color:"var(--text-2)",lineHeight:"1.6"}}>You'll see their question below</li>
                  <li style={{fontSize:"0.85rem",color:"var(--text-2)",lineHeight:"1.6"}}>Speak your answer or type it — whatever feels natural</li>
                  <li style={{fontSize:"0.85rem",color:"var(--text-2)",lineHeight:"1.6"}}>We'll prepare a draft email for you to review and send back to them</li>
                </ul>
                <p style={{fontSize:"0.82rem",color:"var(--text-3)",lineHeight:"1.6",marginBottom:"8px"}}>We don't store your recording or your answer — it goes straight to their email inbox. No account or password needed.</p>
                <p style={{fontSize:"0.82rem",color:"var(--text-3)",fontStyle:"italic",marginBottom:"12px"}}>Takes about two minutes.</p>
                <button onClick={e=>{e.currentTarget.parentElement.style.display='none'; localStorage.setItem('lsm_u2_seen_welcome','true');}} className="btn btn-primary" style={{width:"100%"}}>Ready — show me the question →</button>
              </div>
            )}
            {/* The question — front and center */}
            <div style={{background:"var(--bg2)",border:"1.5px solid var(--border)",borderRadius:"16px",padding:"28px",marginBottom:"24px",position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",top:0,left:0,right:0,height:"3px",background:"var(--purple)"}} />
              {interviewerName && (
                <div style={{marginBottom:"14px"}}>
                  <div style={{fontSize:"0.82rem",fontWeight:"700",color:"var(--purple)",marginBottom:"4px"}}>
                    <span style={{color:"var(--purple)"}}>{interviewerName}</span> has a question for you:
                  </div>
                  {recipientName && (
                    <div style={{fontSize:"1rem",fontWeight:"700",color:"var(--text)"}}>Hi <span style={{color:"var(--text-2)"}}>{recipientName}</span>,</div>
                  )}
                </div>
              )}
              <div style={{fontSize:"1.25rem",fontWeight:"700",color:"var(--text)",lineHeight:"1.5",letterSpacing:"-0.01em",fontStyle:"italic"}}>
                "{customQuestionText.trim().endsWith("?") ? customQuestionText : customQuestionText + "?"}"
              </div>
            </div>

            {/* Instructions */}
            <p style={{fontSize:"0.92rem",color:"var(--text-2)",lineHeight:"1.7",marginBottom:"6px",fontWeight:"400"}}>
              Speak your answer and we'll transcribe it — or type it yourself. Then we'll prepare an email draft for you to review and send back to {interviewerName || "the person who asked"}.
            </p>
            <p style={{fontSize:"0.82rem",color:"var(--text-3)",lineHeight:"1.6",marginBottom:"20px",fontWeight:"400"}}>
              We never send anything without you seeing it first, and we don't store your recording or transcript.
            </p>

            {/* Only ask name if we don't already have it */}
            {!recipientName && (
              <div className="field">
                <label className="field-label">Your name</label>
                <input className="input" placeholder="e.g. Grandma Rose" value={recipientName} onChange={e=>setRecipientName(e.target.value)} />
              </div>
            )}

            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"8px",marginBottom:"16px"}}>
              <button className="record-circle-btn" onClick={()=>{ startInterview(); setTimeout(()=>{ startSpeech(); setIsRecording(true); setHasAnswered(true); }, 300); }}>
                <span style={{fontSize:"28px",lineHeight:"1"}}>🎙</span>
              </button>
              <span style={{fontSize:"0.72rem",fontWeight:"700",color:"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.08em"}}>Tap to Record</span>
              <button onClick={()=>{ setTypeMode(true); startInterview(); }} style={{background:"none",border:"none",cursor:"pointer",fontSize:"0.8rem",color:"var(--purple)",fontFamily:"'Lexend',sans-serif",fontWeight:"600",textDecoration:"underline",marginTop:"4px"}}>
                I'd rather type than speak
              </button>
            </div>

            <FAQ title="Questions about this app" />
          </div>
        )}

        {/* ── INTERVIEW ── */}
        {screen==="interview" && (
          <div className="page fade">
            <div className={isCustomMode ? "page-narrow" : "interview-layout"} style={isCustomMode ? {margin:"0 auto",padding:"0"} : {}}>
              {/* Sidebar — hidden for custom question mode */}
              {!isCustomMode && (
                <div className="interview-sidebar">
                  <div className="sidebar-module">{selectedModule.label}</div>
                  <div className="sidebar-progress">
                    <div className="sidebar-progress-fill" style={{width:`${progressPct}%`}} />
                  </div>
                  <div className="sidebar-q-list">
                    {questions.map((q,i)=>(
                      <div key={q.id} className={`sidebar-q-item ${i===qIndex?"current":""} ${transcript.some(t=>t.id===q.id)?"answered":""}`}>
                        {i+1}. {q.text.length > 60 ? q.text.slice(0,60)+"…" : q.text}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Main */}
              <div>
                {/* Recording status bar */}
                {mediaRecording && (
                  <div className={`rec-bar live`}>
                    <div className="rec-dot live" />
                    <span className="rec-status-text live">{recMode === "video" ? "Video" : "Audio"} recording in progress</span>
                    <span className="rec-timer">{formatTime(elapsed)}</span>
                  </div>
                )}

                {/* Video preview */}
                {recMode==="video" && (
                  <div className="video-preview-wrap" style={{marginBottom:"16px"}}>
                    {mediaRecording
                      ? <video ref={videoPreviewRef} className="video-preview" muted autoPlay playsInline />
                      : <span className="video-preview-placeholder">Video will appear here when recording starts</span>
                    }
                  </div>
                )}

                {/* Question card */}
                <div className="q-card" key={currentQ?.id || "custom"}>
                  {isCustomMode ? (
                    <div className="q-text" style={{fontSize:"1.4rem",lineHeight:"1.4"}}>
                      {customQuestionText.trim().endsWith("?") ? customQuestionText : customQuestionText + "?"}
                    </div>
                  ) : (
                    <>
                      <div className="q-eyebrow">Question {qIndex+1} of {questions.length}</div>
                      <div className="q-text">{currentQ?.text}</div>
                      {currentQ?.subQuestions?.length > 0 && (
                        <ul style={{marginTop:"14px",paddingLeft:"0",listStyle:"none",display:"flex",flexDirection:"column",gap:"6px"}}>
                          {currentQ.subQuestions.map((sq,i)=>(
                            <li key={i} style={{fontSize:"0.82rem",color:"var(--text-3)",lineHeight:"1.5",paddingLeft:"14px",position:"relative"}}>
                              <span style={{position:"absolute",left:0,color:"var(--purple)",fontWeight:"700"}}>·</span>
                              {sq}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>

                {/* Typing mode */}
                {typeMode ? (
                  <div style={{marginBottom:"16px"}}>
                    <label style={{display:"block",fontSize:"0.88rem",fontWeight:"700",color:"var(--text)",marginBottom:"4px"}}>Type your answer</label>
                    {!isCustomMode && (
                      <p style={{fontSize:"0.75rem",color:"var(--text-3)",marginBottom:"8px",lineHeight:"1.5"}}>You'll answer each question one at a time — type your answer below and tap Save to move to the next one.</p>
                    )}
                    {isCustomMode && (
                      <p style={{fontSize:"0.75rem",color:"var(--text-3)",marginBottom:"8px",lineHeight:"1.5"}}>Type your answer below — you'll be able to edit it before sending.</p>
                    )}
                    <textarea
                      className="input"
                      style={{minHeight:"160px",resize:"vertical",fontSize:"0.95rem",lineHeight:"1.7",width:"100%",border:"1.5px solid var(--border)",borderRadius:"8px",padding:"10px 12px"}}
                      placeholder="Write as much or as little as you like."
                      value={typedAnswer}
                      onChange={e=>setTypedAnswer(e.target.value)}
                    />
                    <button
                      className="btn btn-primary"
                      style={{marginTop:"12px",width:"100%"}}
                      disabled={!typedAnswer.trim()}
                      onClick={()=>{
                        const q = isCustomMode ? customQuestionText : (currentQ?.text || "");
                        const entry = { id: isCustomMode ? "custom_q" : (currentQ?.id || "typed"), question: q, answer: typedAnswer.trim() };
                        if (isCustomMode) {
                          setTranscript([entry]);
                          setCleanedTranscript(typedAnswer.trim());
                          setTypedAnswer("");
                          setScreen("finishing");
                        } else {
                          setCurrentAnswer(typedAnswer.trim());
                          capturedAnswerRef.current = typedAnswer.trim();
                          setHasAnswered(true);
                          setTypedAnswer("");
                          goNext();
                        }
                      }}
                    >
                      {isCustomMode ? "Continue to review →" : "Save answer and continue →"}
                    </button>
                    <button onClick={()=>setTypeMode(false)} style={{background:"none",border:"none",cursor:"pointer",fontSize:"0.8rem",color:"var(--purple)",fontFamily:"'Lexend',sans-serif",fontWeight:"600",textDecoration:"underline",padding:"8px 0 0",display:"block",width:"100%",textAlign:"center"}}>
                      Prefer to speak instead?
                    </button>
                  </div>
                ) : (
                  <>
                {/* Recording zone — Whisper based */}
                <div style={{textAlign:"center",padding:"20px 16px",background:"var(--bg2)",border:isRecording?"1.5px solid var(--red)":isTranscribing?"1.5px solid var(--purple)":"1.5px solid var(--border)",borderRadius:"14px",transition:"border-color 0.2s",marginBottom:"0"}}>
                  {isTranscribing ? (
                    <>
                      <div style={{display:"flex",gap:"6px",justifyContent:"center",marginBottom:"12px"}}>
                        {[0,1,2,3,4].map(i=>(
                          <div key={i} style={{width:"6px",height:"6px",borderRadius:"50%",background:"var(--purple)",animation:`bounce 1.2s ease-in-out ${i*0.15}s infinite`}} />
                        ))}
                      </div>
                      <div style={{fontSize:"0.9rem",fontWeight:"700",color:"var(--purple)",marginBottom:"4px"}}>Transcribing your answer…</div>
                      <div style={{fontSize:"0.76rem",color:"var(--text-3)"}}>This usually takes 5–15 seconds</div>
                    </>
                  ) : isRecording ? (
                    <>
                      <div style={{fontSize:"0.82rem",fontWeight:"800",color:"var(--red)",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:"10px",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px"}}>
                        <span style={{width:"10px",height:"10px",borderRadius:"50%",background:"var(--red)",display:"inline-block",animation:"pulse 1.2s infinite"}} />
                        RECORDING
                      </div>
                      <div style={{fontSize:"3rem",fontWeight:"800",lineHeight:"1",marginBottom:"4px",fontVariantNumeric:"tabular-nums",color: recordingElapsed >= 45 ? "#f97316" : "var(--red)"}}>
                        {formatTime(recordingElapsed)} / 1:00
                      </div>
                      {recordingElapsed >= 45 && (
                        <div style={{fontSize:"0.72rem",color:"#f97316",fontWeight:"700",marginBottom:"4px"}}>Wrapping up — almost at the limit</div>
                      )}
                      <div style={{fontSize:"0.72rem",color:"var(--text-3)",fontWeight:"400",marginTop:"4px"}}>
                        Speak clearly and naturally
                      </div>
                    </>
                  ) : currentAnswer && !isTranscribing ? (
                    <>
                      <div style={{fontSize:"1.5rem",marginBottom:"6px"}}>✓</div>
                      <div style={{fontSize:"0.88rem",fontWeight:"700",color:"var(--green)",marginBottom:"4px"}}>Answer captured</div>
                      <div style={{fontSize:"0.75rem",color:"var(--text-3)",lineHeight:"1.5"}}>Press Done ✓ to review and send, or record again to replace your answer.</div>
                    </>
                  ) : isTranscribing ? (
                    <>
                      <div style={{display:"flex",gap:"6px",justifyContent:"center",marginBottom:"12px"}}>
                        {[0,1,2,3,4].map(i=>(
                          <div key={i} style={{width:"6px",height:"6px",borderRadius:"50%",background:"var(--purple)",animation:`bounce 1.2s ease-in-out ${i*0.15}s infinite`}} />
                        ))}
                      </div>
                      <div style={{fontSize:"0.88rem",fontWeight:"600",color:"var(--purple)",marginBottom:"4px"}}>Transcribing your answer…</div>
                      <div style={{fontSize:"0.76rem",color:"var(--text-3)"}}>This usually takes 5–15 seconds</div>
                    </>
                  ) : (
                    <>
                      <div style={{fontSize:"0.84rem",color:"var(--text-3)",fontStyle:"italic",marginBottom:"4px"}}>Tap Record and speak your answer.</div>
                      <div style={{fontSize:"0.72rem",color:"var(--text-3)"}}>Up to 60 seconds — speak clearly and naturally</div>
                      <div style={{fontSize:"0.72rem",color:"var(--text-3)"}}>
                        {isCustomMode ? "Take as long as you need — 1–3 minutes is typical" : "A good answer is usually 1–3 minutes of speaking"}
                      </div>
                    </>
                  )}
                </div>

                {/* Record button row — evenly spaced */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",gap:"8px"}}>
                  <button
                    className="btn btn-ghost"
                    style={{flex:"1",padding:"12px 8px",fontSize:"0.72rem",color:"var(--text-3)",textAlign:"center"}}
                    onClick={()=>{
                      if(window.confirm("This will erase your answers so far. Are you sure you want to start over?")) {
                        window.location.reload();
                      }
                    }}
                  >↺ Start Over</button>

                  <div className="record-btn-col" style={{flex:"0 0 auto"}}>
                    <button className={`record-circle-btn ${isRecording?"recording":""}`} onClick={toggleSpeech}>
                      {isRecording
                        ? <div className="record-circle-inner recording" />
                        : <span className="record-circle-inner">🎙</span>
                      }
                    </button>
                    <span className={`record-btn-label ${isRecording?"live":""}`}>{isRecording?"Stop":"Record"}</span>
                  </div>

                  <button
                    className={`btn ${hasAnswered?"btn-secondary":"btn-ghost"}`}
                    style={{flex:"1",padding:"12px 8px",fontSize:"0.82rem",textAlign:"center",opacity:(isRecording||isTranscribing)?0.35:1}}
                    disabled={isRecording||isTranscribing}
                    onClick={()=>{
                      if (hasAnswered && isCustomMode) {
                        setScreen("finishing");
                      } else if (hasAnswered) {
                        goNext();
                      } else {
                        goSkip();
                      }
                    }}
                  >
                    {hasAnswered ? (isCustomMode ? "Done ✓" : "Next →") : "Skip →"}
                  </button>
                </div>
                </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── FINISHING ── */}
        {!isEventMode && screen==="finishing" && (
          <div className="page page-narrow fade">
            <div className="section-eyebrow">Almost done</div>
            <h1 className="screen-title">Wrap up your interview.</h1>
            <p className="screen-sub">Your answer has been captured. Review it below, then send it to {interviewerName || "the person who asked"}.</p>

            {mediaRecording && (
              <button className="btn btn-danger" style={{marginBottom:"20px"}} onClick={async ()=>{ await stopMediaRecording(); }}>
                ⏹ Stop Recording
              </button>
            )}

            {/* Email gate */}
            {/* Show transcript for review */}
            {(cleanedTranscript || (transcript.length > 0 && !isCleaning)) && (
              <div style={{background:"var(--bg2)",border:"1.5px solid var(--border)",borderRadius:"12px",padding:"20px",marginBottom:"20px"}}>
                <div style={{fontSize:"0.65rem",fontWeight:"700",letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--text-3)",marginBottom:"8px"}}>Question & Answer</div>
                {(customQuestionText || (transcript.length > 0 && transcript[0].question)) && (
                  <div style={{fontSize:"0.88rem",fontWeight:"700",color:"var(--text-2)",marginBottom:"12px",paddingBottom:"10px",borderBottom:"1px solid var(--border)",fontStyle:"italic"}}>
                    "{customQuestionText || transcript[0].question}"
                  </div>
                )}
                <div style={{fontSize:"0.78rem",fontWeight:"600",color:"var(--purple)",marginBottom:"8px"}}>✏ Tap the text below to edit before sending</div>
                {isCleaning ? (
                  <div style={{textAlign:"center",padding:"20px 0",color:"var(--purple)",fontSize:"0.84rem",fontWeight:"600"}}>✦ Polishing your transcript…</div>
                ) : (
                  <div style={{position:"relative"}}>
                    <textarea
                      style={{width:"100%",border:"1.5px solid var(--border)",borderRadius:"8px",padding:"12px",fontFamily:"'Lexend',sans-serif",fontSize:"0.92rem",color:"var(--text)",lineHeight:"1.8",background:"var(--bg)",resize:"vertical",minHeight:"150px",overflowY:"auto",outline:"none"}}
                      value={cleanedTranscript || transcript.map(e=>e.answer).join("\n\n")}
                      onChange={e=>setCleanedTranscript(e.target.value)}
                    />
                    <div style={{fontSize:"0.68rem",color:"var(--text-3)",textAlign:"center",marginTop:"4px",fontStyle:"italic"}}>Scroll to see full answer · Drag corner to resize</div>
                  </div>
                )}
              </div>
            )}

            {/* Email gate — simplified */}
            <div style={{marginBottom:"16px"}}>
              <div style={{fontSize:"0.78rem",color:"var(--purple)",background:"var(--purple-light)",borderRadius:"8px",padding:"10px 14px",marginBottom:"16px",lineHeight:"1.6",fontWeight:"500"}}>
                Nothing is sent automatically — we'll open a draft for you to review and send.
              </div>

              {/* Optional email */}
              <label style={{display:"block",fontSize:"0.82rem",fontWeight:"600",color:"var(--text)",marginBottom:"4px"}}>
                Want a copy for yourself? <span style={{fontWeight:"400",color:"var(--text-3)"}}>Optional</span>
              </label>
              <p style={{fontSize:"0.75rem",color:"var(--text-3)",marginBottom:"8px",lineHeight:"1.5"}}>Enter your email and we'll CC you on the answer — or sign up to use Life Story Magic yourself for free.</p>
              <input
                id="finishing-email-input"
                className="input"
                type="email"
                placeholder="your@email.com (optional)"
                value={emailInput}
                onChange={e=>setEmailInput(e.target.value)}
                style={{marginBottom:"10px"}}
              />

              <label className="checkbox-row" style={{marginBottom:"4px"}}>
                <input type="checkbox" checked={marketingConsent} onChange={e=>setMarketingConsent(e.target.checked)} />
                <span className="checkbox-label">👈 ✉️ This box is not checked by default, because you don't <strong>have</strong> to sign up for our emails. But I think you'll like them — and it will help us grow Life Story Magic and make this better for everyone. Check the box to subscribe. By checking it you agree to our <a href="https://docs.google.com/document/d/1yTiM7VGVMMoQ08Ai0rRUrJDLFFipZHNuWFSWiyBilHQ/" target="_blank" rel="noreferrer">Terms &amp; Privacy</a>.</span>
              </label>
              <p style={{fontSize:"0.72rem",color:"var(--text-3)",marginBottom:"16px",lineHeight:"1.5"}}>Optional. You can send your answer whether or not you check this box.</p>

              {!emailSent ? (
                <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                  <button className="btn btn-primary" style={{fontSize:"1rem",padding:"18px"}} onClick={async ()=>{
                    if (emailInput.trim() && !cleanedTranscript) await cleanTranscriptWithClaude(buildTranscript());
                    if (!cleanedTranscript) await cleanTranscriptWithClaude(buildTranscript());
                    if (marketingConsent && emailInput.trim()) {
                      try { await fetch("/.netlify/functions/subscribe", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email: emailInput.trim() }) }); } catch(e) {}
                    }
                    // Save User 2 details for future use as User 1
                    if (emailInput.trim()) localStorage.setItem('lsm_u1_email', emailInput.trim());
                    if (recipientName.trim()) localStorage.setItem('lsm_u1_name', recipientName.trim());
                    sendResultEmail();
                  }}>
                    {mode==="send_interview" ? `Compose email to ${interviewerName || "them"} →` : "Compose email →"}
                  </button>
                  <button className="btn btn-secondary" style={{width:"100%"}} onClick={async ()=>{
                    if (!cleanedTranscript) await cleanTranscriptWithClaude(buildTranscript());
                    copyEmailText(cleanedTranscript || buildTranscript() || "");
                  }}>
                    📋 Copy email text instead
                  </button>
                  <p style={{fontSize:"0.75rem",color:"var(--text-3)",textAlign:"center",marginTop:"4px",lineHeight:"1.5"}}>Your mail app will open with a draft — review it and hit Send when ready. Or copy and paste into any email.</p>
                </div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
                  <div style={{background:"var(--green-dim)",border:"1px solid rgba(22,163,74,0.2)",borderRadius:"10px",padding:"14px 16px",fontSize:"0.88rem",color:"var(--green)",fontWeight:"600",lineHeight:"1.6"}}>
                    ✓ Your mail app opened — review the draft and hit Send when you're ready.
                  </div>
                  <div style={{background:"var(--purple-light)",border:"1.5px solid rgba(100,50,196,0.25)",borderRadius:"12px",padding:"20px",textAlign:"center"}}>
                    <div style={{fontSize:"1rem",fontWeight:"800",color:"var(--text)",marginBottom:"8px",letterSpacing:"-0.02em"}}>Want to ask someone a question of your own?</div>
                    <p style={{fontSize:"0.85rem",color:"var(--text-2)",lineHeight:"1.6",marginBottom:"14px"}}>Life Story Magic is free to use — no account, no password. Ask anyone anything, capture the answer, keep it forever.</p>
                    <a href="https://app.lifestorymagic.com" style={{display:"block",background:"var(--purple)",color:"#fff",borderRadius:"10px",padding:"14px",fontFamily:"'Lexend',sans-serif",fontWeight:"800",fontSize:"0.95rem",textDecoration:"none"}}>Ask your own question →</a>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ── TRANSCRIPT VIEW ── */}
        {!isEventMode && screen==="transcript" && (
          <div className="page fade">
            <span className="back-link" onClick={()=>setScreen("finishing")}>← Back</span>
            <div className="t-header">
              <div className="t-subject">{subjectName || recipientName || "Interview"}</div>
              <div className="t-meta">
                {selectedModule.label} · {transcript.length} answer{transcript.length!==1?"s":""}{interviewerName?` · arranged by ${interviewerName}`:""} · {new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
              </div>
            </div>
            <div className="t-divider" />
            {transcript.length===0
              ? <div className="t-empty">No answers captured yet.</div>
              : transcript.map((entry,i)=>{
                  const marker = markers.find(m=>m.qIndex===i);
                  return (
                    <div key={i} className="t-entry">
                      <div className="t-timestamp">{marker?`[${formatTime(marker.time)}] `:""}{`Q${String(i+1).padStart(2,"0")}`}</div>
                      <div className="t-q">{entry.question}</div>
                      <div className="t-a">{entry.answer}</div>
                    </div>
                  );
                })
            }
            <div className="divider" />
            <div className="btn-stack">
              <button className="btn btn-primary" onClick={()=>setScreen("finishing")}>Download &amp; Send →</button>
            </div>

            {/* Promo */}
            <div className="promo-box">
              <div className="promo-eyebrow">Go deeper</div>
              <div className="promo-title">A professional interview, beautifully done.</div>
              <p className="promo-body">Our experienced interviewers at Life Story Magic conduct full life story sessions by video — guided, recorded, and beautifully transcribed for your family to keep forever.</p>
              <div className="promo-code-chip">FREEAPP</div>
              <p className="promo-code-sub">Use this code for 20% off your first session.</p>
              <a href="https://store.lifestorymagic.com/checkouts/cn/hWNE8KFqNVpPecBv8nkIdJLu/en-us?_r=AQABkPE1ivYhe43S8MiBR6dyP2PZ7jFjDIOIxxitPhKZrtU" target="_blank" rel="noreferrer" style={{display:"block",width:"100%",padding:"14px",background:"var(--purple)",color:"#fff",borderRadius:"12px",fontFamily:"Lexend,sans-serif",fontWeight:"800",fontSize:"0.92rem",textDecoration:"none",textAlign:"center"}}>Book a Professional Interview</a>
            </div>
          </div>
        )}
      </div>

      {/* Ideas modal */}
      {!isEventMode && ideasOpen && (
        <div className="ideas-overlay" onClick={e=>{if(e.target===e.currentTarget)setIdeasOpen(false);}}>
          <div className="ideas-modal">
            <div className="ideas-title">Need some inspiration?</div>
            <div className="ideas-sub">Tap any question to use it — then edit it to make it your own.</div>
            {EXAMPLE_QUESTIONS.map((q,i)=>(
              <button key={i} className="ideas-chip" onClick={()=>{ setCustomQuestionText(q.slice(0,100)); setIdeasOpen(false); setShowTypeForm(true); }}>
                {q}
              </button>
            ))}
            <button style={{marginTop:"8px",background:"none",border:"none",cursor:"pointer",fontSize:"0.82rem",color:"var(--text-3)",fontFamily:"'Lexend',sans-serif",width:"100%",textAlign:"center"}} onClick={()=>setIdeasOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Info modal */}
      {!isEventMode && infoOpen && (
        <div className="info-overlay" onClick={e=>{if(e.target===e.currentTarget)setInfoOpen(false);}}>
          <div className="info-sheet">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"12px"}}>
              <h2 style={{fontSize:"1.2rem",fontWeight:"900",color:"var(--text)",letterSpacing:"-0.03em",margin:0}}>Welcome to Life Story Magic!</h2>
              <button onClick={()=>setInfoOpen(false)} style={{background:"var(--bg)",border:"1.5px solid var(--border)",borderRadius:"50%",width:"30px",height:"30px",cursor:"pointer",fontFamily:"'Lexend',sans-serif",fontWeight:"700",fontSize:"0.95rem",color:"var(--text)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginLeft:"8px"}}>✕</button>
            </div>
            <p style={{fontSize:"0.85rem",color:"var(--text-2)",lineHeight:"1.6",marginBottom:"12px"}}>Our mission is to help people discover, save, and share their most important stories — one question at a time. No account, login, or password required.</p>

            <div style={{fontSize:"0.75rem",fontWeight:"700",color:"var(--text)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"8px"}}>How it works:</div>
            <ol style={{margin:"0 0 12px",paddingLeft:"18px",display:"flex",flexDirection:"column",gap:"6px"}}>
              <li style={{fontSize:"0.82rem",color:"var(--text-2)",lineHeight:"1.5"}}>Choose someone and pick a question.</li>
              <li style={{fontSize:"0.82rem",color:"var(--text-2)",lineHeight:"1.5"}}>We open a ready-to-send message. You choose the person from your contacts and press Send.</li>
              <li style={{fontSize:"0.82rem",color:"var(--text-2)",lineHeight:"1.5"}}>They speak or type their answer and send it back.</li>
              <li style={{fontSize:"0.82rem",color:"var(--text-2)",lineHeight:"1.5"}}>The answer lands in your email inbox. We don't store a copy.</li>
            </ol>

            <div style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:"10px",padding:"14px",marginBottom:"16px"}}>
              <p style={{fontSize:"0.85rem",color:"var(--text-2)",lineHeight:"1.7",margin:0}}><strong>This is a Private Beta.</strong> Things may not be perfect yet — if something seems off, please email us at <a href="mailto:feedback@lifestorymagic.com" style={{color:"var(--purple)"}}>feedback@lifestorymagic.com</a>. Your input helps us make this better for everyone.</p>
            </div>

            <div style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:"10px",padding:"10px 14px",marginBottom:"12px"}}>
              <p style={{fontSize:"0.78rem",color:"var(--text-2)",lineHeight:"1.6",margin:"0 0 8px"}}>Free to use — help us keep it that way:</p>
              <div style={{display:"flex",flexDirection:"column",gap:"4px"}}>
                <a href="https://store.lifestorymagic.com/products/life-story-magic-interview" target="_blank" rel="noreferrer" style={{fontSize:"0.78rem",color:"var(--purple)",fontWeight:"600",textDecoration:"none"}}>🎬 Book a professional interview (code FREEAPP) →</a>
                <a href="https://lifestorymagic.gumroad.com/l/27questions" target="_blank" rel="noreferrer" style={{fontSize:"0.78rem",color:"var(--purple)",fontWeight:"600",textDecoration:"none"}}>📖 Get our ebook →</a>
                <a href="https://buymeacoffee.com/lifestorymagic" target="_blank" rel="noreferrer" style={{fontSize:"0.78rem",color:"var(--purple)",fontWeight:"600",textDecoration:"none"}}>☕ Buy me a coffee →</a>
              </div>
            </div>

            <p style={{fontSize:"0.82rem",color:"var(--text-2)",lineHeight:"1.6",marginBottom:"12px"}}>
              Happy saving! — <a href="https://www.linkedin.com/in/billmurphyjr/" target="_blank" rel="noreferrer" style={{color:"var(--purple)",textDecoration:"none",fontWeight:"600"}}>Bill Murphy Jr.</a>, Founder
            </p>
            <button className="btn btn-primary" style={{width:"100%"}} onClick={()=>setInfoOpen(false)}>Got it, let's go →</button>
          </div>
        </div>
      )}


      {/* ── EVENT MODE ── */}
      {isEventMode && (
        <div style={{maxWidth:"600px",margin:"0 auto",padding:"0 20px 40px"}}>

          {/* EVENT MODE BANNER */}
          <div style={{background:"#1a1626",color:"#fff",textAlign:"center",padding:"8px 16px",fontSize:"0.72rem",fontWeight:"700",letterSpacing:"0.14em",textTransform:"uppercase"}}>
            ★ EVENT MODE — ORGANIZER VIEW
          </div>

          {/* SETUP */}
          {eventScreen === "setup" && (
            <div className="fade" style={{paddingTop:"24px"}}>
              <h1 style={{fontSize:"1.8rem",fontWeight:"800",letterSpacing:"-0.03em",color:"var(--text)",marginBottom:"8px",lineHeight:"1.2"}}>Event Mode</h1>
              <p style={{fontSize:"0.92rem",color:"var(--text-2)",lineHeight:"1.7",marginBottom:"24px"}}>Ask one question, collect spoken answers from everyone at your event. Audio downloads automatically as backup after each recording. Responses are saved to your browser as you go.</p>
              <div className="field">
                <label className="field-label">Your name</label>
                <input className="input" placeholder="e.g. Bill" value={eventOrganizerName} onChange={e=>setEventOrganizerName(e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Your email</label>
                <input className="input" type="email" placeholder="you@example.com" value={eventOrganizerEmail} onChange={e=>setEventOrganizerEmail(e.target.value)} />
              </div>
              <button className="btn btn-primary" disabled={!eventOrganizerName.trim()||!eventOrganizerEmail.trim()} onClick={()=>setEventScreen("question")} style={{marginTop:"8px"}}>
                Next: Set your question →
              </button>
            </div>
          )}

          {/* SET QUESTION */}
          {eventScreen === "question" && (
            <div className="fade" style={{paddingTop:"24px"}}>
              <div style={{fontSize:"0.65rem",fontWeight:"700",letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--purple)",marginBottom:"8px"}}>Step 1 of 2</div>
              <h2 style={{fontSize:"1.4rem",fontWeight:"800",color:"var(--text)",marginBottom:"8px",letterSpacing:"-0.02em"}}>What do you want to ask everyone?</h2>
              <p style={{fontSize:"0.88rem",color:"var(--text-2)",lineHeight:"1.65",marginBottom:"16px"}}>Type it, or record it and edit afterward. Everyone at the event answers this same question.</p>

              <div className="field" style={{marginBottom:"12px"}}>
                <label className="field-label">Your question (editable)</label>
                <textarea
                  className="input"
                  style={{minHeight:"80px",resize:"vertical",fontSize:"0.95rem",lineHeight:"1.6"}}
                  placeholder={'e.g. "What is your favorite memory with our family?"'}
                  value={eventQuestion}
                  onChange={e=>setEventQuestion(e.target.value)}
                />
              </div>

              <div style={{textAlign:"center",background:"var(--bg)",border:"1.5px solid var(--border)",borderRadius:"12px",padding:"16px",marginBottom:"16px"}}>
                <div style={{fontSize:"0.72rem",color:"var(--text-3)",fontWeight:"600",marginBottom:"10px",textTransform:"uppercase",letterSpacing:"0.08em"}}>Or speak it — we'll transcribe it into the field above</div>
                {isEventTranscribing ? (
                  <div style={{fontSize:"0.84rem",color:"var(--purple)",fontWeight:"600"}}>✦ Transcribing…</div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"6px"}}>
                    <button
                      className={`record-circle-btn ${isEventRecording?"recording":""}`}
                      style={{width:"60px",height:"60px"}}
                      onClick={async ()=>{
                        if (isEventRecording) {
                          const t = await stopEventRecording();
                          if (t) setEventQuestion(t);
                        } else { await startEventRecording(); }
                      }}
                    >
                      {isEventRecording ? <div className="record-circle-inner recording" /> : <span style={{fontSize:"22px",lineHeight:"1"}}>🎙</span>}
                    </button>
                    <span style={{fontSize:"0.68rem",fontWeight:"700",color:isEventRecording?"var(--red)":"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.06em"}}>
                      {isEventRecording ? "Tap to stop" : "Record"}
                    </span>
                  </div>
                )}
              </div>

              <button className="btn btn-primary" disabled={!eventQuestion.trim()} onClick={()=>setEventScreen("responses")}>
                Next: Start collecting answers →
              </button>
            </div>
          )}

          {/* COLLECT RESPONSES */}
          {eventScreen === "responses" && (
            <div className="fade" style={{paddingTop:"16px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"8px"}}>
                <div style={{fontSize:"0.65rem",fontWeight:"700",letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--purple)"}}>
                  {eventResponses.length} response{eventResponses.length!==1?"s":""} collected
                </div>
                {eventResponses.length > 0 && (
                  <button style={{background:"none",border:"none",cursor:"pointer",fontSize:"0.75rem",color:"var(--purple)",fontFamily:"'Lexend',sans-serif",fontWeight:"600",textDecoration:"underline"}} onClick={()=>{
                    const partial = eventResponses.join("\n\n---\n\n");
                    const subject = encodeURIComponent(`Event responses so far — "${eventQuestion.slice(0,40)}"`);
                    const body = encodeURIComponent(`Question: "${eventQuestion}"\n\nResponses so far (${eventResponses.length}):\n\n${partial}\n\nLife Story Magic`);
                    const a = document.createElement("a"); a.href=`mailto:${eventOrganizerEmail}?subject=${subject}&body=${body}`; a.target="_blank"; document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  }}>Save progress →</button>
                )}
              </div>

              <div style={{background:"var(--purple-light)",borderRadius:"10px",padding:"10px 14px",marginBottom:"16px",fontSize:"0.88rem",color:"var(--purple)",fontWeight:"600",fontStyle:"italic"}}>
                "{eventQuestion}"
              </div>

              <p style={{fontSize:"0.82rem",color:"var(--text-2)",lineHeight:"1.6",marginBottom:"16px"}}>
                Pass the device to each person. Ask them to <strong>say their name and spell it</strong>, then answer the question. Audio downloads automatically as backup.
              </p>

              <div style={{textAlign:"center",background:"var(--bg2)",border:isEventRecording?"1.5px solid var(--red)":isEventTranscribing?"1.5px solid var(--purple)":"1.5px solid var(--border)",borderRadius:"14px",padding:"20px",marginBottom:"12px"}}>
                {isEventTranscribing ? (
                  <><div style={{fontSize:"1.5rem",marginBottom:"8px"}}>✦</div><div style={{fontSize:"0.84rem",fontWeight:"700",color:"var(--purple)"}}>Transcribing and saving audio backup…</div></>
                ) : eventCurrentTranscript ? (
                  <>
                    <div style={{fontSize:"1.2rem",marginBottom:"6px"}}>✓</div>
                    <div style={{fontSize:"0.84rem",fontWeight:"600",color:"var(--green)",marginBottom:"8px"}}>Response captured — audio file downloaded</div>
                    <div style={{fontSize:"0.82rem",color:"var(--text-2)",lineHeight:"1.6",textAlign:"left",background:"var(--bg)",borderRadius:"8px",padding:"10px 12px"}}>
                      {eventCurrentTranscript.slice(0,300)}{eventCurrentTranscript.length>300?"…":""}
                    </div>
                  </>
                ) : isEventRecording ? (
                  <>
                    <div style={{fontSize:"2.2rem",fontWeight:"800",color:"var(--red)",marginBottom:"4px",fontVariantNumeric:"tabular-nums"}}>{formatTime(eventRecordingElapsed)}</div>
                    <div style={{fontSize:"0.72rem",color:"var(--red)",fontWeight:"700",textTransform:"uppercase",letterSpacing:"0.06em"}}>● Recording</div>
                    <div style={{fontSize:"0.75rem",color:"var(--text-3)",marginTop:"8px"}}>30 seconds to 3 minutes is ideal</div>
                  </>
                ) : (
                  <div style={{fontSize:"0.84rem",color:"var(--text-3)",fontStyle:"italic"}}>Pass device to next person, then tap Record</div>
                )}
              </div>

              {!eventCurrentTranscript ? (
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"8px",marginBottom:"16px"}}>
                  <button
                    className={`record-circle-btn ${isEventRecording?"recording":""}`}
                    onClick={async ()=>{
                      if (isEventRecording) {
                        const t = await stopEventRecordingWithDownload(eventResponses.length + 1);
                        if (t) setEventCurrentTranscript(t);
                      } else {
                        setEventCurrentTranscript("");
                        await startEventRecording();
                      }
                    }}
                  >
                    {isEventRecording ? <div className="record-circle-inner recording" /> : <span style={{fontSize:"28px",lineHeight:"1"}}>🎙</span>}
                  </button>
                  <span style={{fontSize:"0.72rem",fontWeight:"700",color:isEventRecording?"var(--red)":"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.06em"}}>
                    {isEventRecording ? "Tap to stop" : "Record next person"}
                  </span>
                </div>
              ) : (
                <div style={{display:"flex",gap:"10px",marginBottom:"16px"}}>
                  <button className="btn btn-primary" style={{flex:1}} onClick={()=>{
                    const updated = [...eventResponses, eventCurrentTranscript];
                    setEventResponses(updated);
                    try { localStorage.setItem("lsm_event_responses", JSON.stringify({question:eventQuestion,responses:updated,organizer:eventOrganizerName,email:eventOrganizerEmail,savedAt:new Date().toISOString()})); } catch(e) {}
                    setEventCurrentTranscript("");
                  }}>Add &amp; record next ✓</button>
                  <button className="btn btn-secondary" onClick={()=>setEventCurrentTranscript("")}>Re-record</button>
                </div>
              )}

              {eventResponses.length > 0 && !isEventRecording && !isEventTranscribing && (
                <div style={{marginTop:"8px",paddingTop:"16px",borderTop:"1px solid var(--border)"}}>
                  <div style={{fontSize:"0.78rem",color:"var(--text-3)",marginBottom:"8px",textAlign:"center"}}>{eventResponses.length} response{eventResponses.length!==1?"s":""} ready to compile</div>
                  <button className="btn btn-primary" onClick={async ()=>{
                    setIsAggregating(true);
                    await aggregateEventResponses(eventResponses, eventQuestion);
                    setEventScreen("result");
                  }}>
                    Done — compile all responses →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* RESULT */}
          {eventScreen === "result" && (
            <div className="fade" style={{paddingTop:"24px"}}>
              <div style={{fontSize:"0.65rem",fontWeight:"700",letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--purple)",marginBottom:"8px"}}>Complete</div>
              <h2 style={{fontSize:"1.4rem",fontWeight:"800",color:"var(--text)",marginBottom:"16px",letterSpacing:"-0.02em"}}>{eventResponses.length} responses compiled.</h2>
              {isAggregating ? (
                <div style={{textAlign:"center",padding:"40px 0"}}>
                  <div style={{fontSize:"1.5rem",marginBottom:"12px"}}>✦</div>
                  <div style={{fontSize:"0.84rem",fontWeight:"700",color:"var(--purple)"}}>Claude is attributing and formatting responses…</div>
                </div>
              ) : (
                <>
                  <div style={{background:"var(--bg2)",border:"1.5px solid var(--border)",borderRadius:"12px",padding:"20px",marginBottom:"16px",fontSize:"0.88rem",lineHeight:"1.9",color:"var(--text)",whiteSpace:"pre-wrap"}}>
                    {eventResult}
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
                    <button className="btn btn-primary" onClick={()=>{
                      const subject = encodeURIComponent(`Event responses — "${eventQuestion.slice(0,50)}"`);
                      const body = encodeURIComponent(`Hi ${eventOrganizerName},\n\nHere are the compiled responses from your event.\n\nQuestion: "${eventQuestion}"\n\n${eventResult}\n\nBrought to you by Life Story Magic`);
                      const a = document.createElement("a"); a.href=`mailto:${eventOrganizerEmail}?subject=${subject}&body=${body}`; a.target="_blank"; document.body.appendChild(a); a.click(); document.body.removeChild(a);
                      try { localStorage.removeItem("lsm_event_responses"); } catch(e) {}
                    }}>
                      Email compiled results to myself →
                    </button>
                    <button className="btn btn-secondary" onClick={()=>{ setEventScreen("responses"); setEventCurrentTranscript(""); }}>
                      Add more responses
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Help Keep This Free + Alpha combined modal */}
      {(helpOpen || alphaOpen) && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}} onClick={e=>{if(e.target===e.currentTarget){setHelpOpen(false);setAlphaOpen(false);}}}>
          <div style={{background:"var(--bg2)",borderRadius:"16px",padding:"28px",maxWidth:"420px",width:"100%",maxHeight:"90vh",overflowY:"auto"}}>
            <h2 style={{fontSize:"1.4rem",fontWeight:"800",color:"var(--text)",marginBottom:"8px",letterSpacing:"-0.02em"}}>Help keep this free!</h2>
            <p style={{fontSize:"0.88rem",color:"var(--text)",lineHeight:"1.7",marginBottom:"16px"}}>Life Story Magic is free to use — and I want to keep it that way. But it costs real money to run: AI transcription, server costs, and a lot of late nights. If it's been useful to you, here are four ways to help:</p>

            <a href="https://store.lifestorymagic.com/products/life-story-magic-interview" target="_blank" rel="noreferrer" style={{display:"block",background:"var(--bg)",border:"1.5px solid var(--border)",borderRadius:"12px",padding:"14px 16px",marginBottom:"8px",textDecoration:"none",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:"0.95rem",fontWeight:"700",color:"var(--text)",marginBottom:"2px"}}>🎬 Get a professional interview</div>
                <div style={{fontSize:"0.78rem",color:"var(--text-2)"}}>Our flagship product. Use code <strong>FREEAPP</strong> for 20% off.</div>
              </div>
              <span style={{fontSize:"0.78rem",color:"var(--purple)",fontWeight:"700",marginLeft:"12px",flexShrink:0}}>→</span>
            </a>

            <a href="https://lifestorymagic.gumroad.com/l/27questions" target="_blank" rel="noreferrer" style={{display:"block",background:"var(--bg)",border:"1.5px solid var(--border)",borderRadius:"12px",padding:"14px 16px",marginBottom:"8px",textDecoration:"none",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:"0.95rem",fontWeight:"700",color:"var(--text)",marginBottom:"2px"}}>📖 Get the ebook</div>

              </div>
              <span style={{fontSize:"0.78rem",color:"var(--purple)",fontWeight:"700",marginLeft:"12px",flexShrink:0}}>→</span>
            </a>

            <a href="https://buymeacoffee.com/lifestorymagic" target="_blank" rel="noreferrer" style={{display:"block",background:"var(--bg)",border:"1.5px solid var(--border)",borderRadius:"12px",padding:"14px 16px",marginBottom:"8px",textDecoration:"none",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:"0.95rem",fontWeight:"700",color:"var(--text)"}}>☕ Buy me a coffee</div>
              <span style={{fontSize:"0.78rem",color:"var(--purple)",fontWeight:"700",marginLeft:"12px",flexShrink:0}}>→</span>
            </a>

            <div style={{background:"var(--bg)",border:"1.5px solid var(--border)",borderRadius:"12px",padding:"16px",marginBottom:"16px"}}>
              <div style={{fontSize:"1rem",fontWeight:"700",color:"var(--text)",marginBottom:"4px"}}>📣 Use it and spread the word</div>
              <div style={{fontSize:"0.82rem",color:"var(--text)",lineHeight:"1.5"}}>Tell a friend. Forward an email. Post about it. The best thing you can do to help Life Story Magic grow is share it with someone who might love it.</div>
            </div>

            <p style={{fontSize:"0.82rem",color:"var(--text)",lineHeight:"1.8",marginBottom:"12px"}}>
              Either way, thank you for using Life Story Magic. It means a lot.<br/>
              — <a href="https://www.linkedin.com/in/billmurphyjr/" target="_blank" rel="noreferrer" style={{color:"var(--purple)",fontWeight:"600",textDecoration:"none"}}>Bill Murphy Jr.</a><br/>
              <span style={{fontSize:"0.75rem",color:"var(--text-3)"}}>Founder, Life Story Magic</span>
            </p>
            <div style={{borderTop:"1px solid var(--border)",paddingTop:"12px",marginBottom:"16px"}}>
              <p style={{fontSize:"0.78rem",color:"var(--text-3)",lineHeight:"1.6"}}>⚠️ <strong>Private Beta:</strong> Still in early development. Found something to fix? Email <a href="mailto:feedback@lifestorymagic.com" style={{color:"var(--purple)"}}>feedback@lifestorymagic.com</a>.</p>
            </div>
            <button className="btn btn-primary" style={{width:"100%"}} onClick={()=>{setHelpOpen(false);setAlphaOpen(false);}}>Thank you — got it!</button>
          </div>
        </div>
      )}

      {/* QSP modal */}
      {qspOpen && (
        <div className="ideas-overlay" onClick={e=>{if(e.target===e.currentTarget)setQspOpen(false);}}>
          <div className="ideas-modal">
            <div style={{fontSize:"1.1rem",fontWeight:"800",color:"var(--text)",marginBottom:"4px",letterSpacing:"-0.02em"}}>Question Suggestion Packs</div>
            <div style={{fontSize:"0.82rem",color:"var(--text-3)",marginBottom:"16px",lineHeight:"1.6"}}>Tap a pack to use it.</div>
            {ALL_MODULES.map((m,i)=>(
              <button key={i} className="ideas-chip" onClick={()=>{
                setSelectedModule(m);
                setCustomQuestionText("");
                setQspOpen(false);
                setMode("send_interview");
                setScreen("setup_send");
              }}>
                <div style={{fontWeight:"700",marginBottom:"3px"}}>{m.label}</div>
                <div style={{fontSize:"0.78rem",fontWeight:"400",color:"var(--text-3)"}}>{m.description}</div>
              </button>
            ))}
            <div style={{fontSize:"0.78rem",color:"var(--text-3)",marginTop:"12px",lineHeight:"1.6",fontStyle:"italic",textAlign:"center"}}>
              This app is in the early stages. More Question Suggestion Packs coming all the time.
            </div>
            <button style={{marginTop:"12px",background:"none",border:"none",cursor:"pointer",fontSize:"0.82rem",color:"var(--text-3)",fontFamily:"'Lexend',sans-serif",width:"100%",textAlign:"center"}} onClick={()=>setQspOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      <FeedbackModal />
    </>
  );
}